import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Icons } from '../utils/icons';
import api, { API_URL, BOT_USERNAME } from '../utils/api';
import { fontStack, glassCard, dInput, btnPrimary, btnSecondary, btnSmall, btnSuccess, btnDanger, btnGhost } from '../styles/shared';
import CustomSelect from './CustomSelect';
import ConfirmModal from './ConfirmModal';
import useConfirm from '../hooks/useConfirm';
import useIsMobile from '../hooks/useIsMobile';
import PaymentsModal from './PaymentsModal';
import SuperAdminDashboard from './SuperAdminDashboard';
import ChannelDMsTab from './ChannelDMsTab';
import VaultTab from './VaultTab';
import SubscriptionsTab from './SubscriptionsTab';
import OverviewTab from './OverviewTab';
import ChattersTab from './ChattersTab';
import GuideTab from './GuideTab';

// State-based channel avatar with proper fallback
function ChannelAvatar({ username, title, size = 28, radius = 7 }) {
    const [failed, setFailed] = useState(false);
    const initial = (title || username || 'C')[0].toUpperCase();
    const wrapStyle = { width: `${size}px`, height: `${size}px`, borderRadius: `${radius}px`, overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg, var(--accent), #0088cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: `${Math.max(9, size * 0.38)}px`, fontWeight: 700 };
    if (failed || !username) return <div style={wrapStyle}>{initial}</div>;
    return (
        <div style={wrapStyle}>
            <img src={`${API_URL}/api/dashboard/channel-photo?channel=${username}`} alt={initial}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={() => setFailed(true)} />
        </div>
    );
}

function ReferralsTab({ isMobile, hasChannels }) {
    const [code, setCode] = useState(null);
    const [stats, setStats] = useState(null);
    const [referred, setReferred] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get('/api/dashboard/referral-stats');
                setStats(res.data ?? null);
                setCode(res.data.code ?? null);
                setReferred(res.data.referrals ?? []);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load referral data');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    async function generateCode() {
        setGenerating(true);
        try {
            const res = await api.post('/api/dashboard/referral-code');
            setCode(res.data.code ?? null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate code');
        } finally {
            setGenerating(false);
        }
    }

    const refLink = code ? `https://privly.co/login?signup=true&ref=${code}` : null;

    function copyLink() {
        if (!refLink) return;
        navigator.clipboard.writeText(refLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }

    function shareTwitter() {
        if (!refLink) return;
        const text = encodeURIComponent(`I'm earning passive income on Privly! Join using my link: ${refLink}`);
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank', 'noopener,noreferrer');
    }

    const cardStyle = {
        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
        borderRadius: '14px', padding: '20px 24px', boxShadow: 'var(--card-shadow)',
    };
    const statCard = (label, value) => (
        <div key={label} style={{ ...cardStyle, flex: 1, minWidth: '120px', textAlign: 'center' }}>
            <p style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>{value ?? '—'}</p>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{label}</p>
        </div>
    );

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading...</p>
        </div>
    );

    if (error) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
            <p style={{ color: 'var(--error, #ef4444)', fontSize: '14px' }}>{error}</p>
        </div>
    );

    return (
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>Referrals</h1>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '0 0 16px' }}>
                Earn a commission when creators you refer sign up and start earning on Privly.
            </p>
            <div style={{ ...cardStyle, marginBottom: '20px', display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '13px' }}>
                <div>
                    <p style={{ color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>First 3 months</p>
                    <p style={{ color: 'var(--text)', margin: 0, fontSize: '18px', fontWeight: 700 }}>5%</p>
                </div>
                <div>
                    <p style={{ color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>After 3 months</p>
                    <p style={{ color: 'var(--text)', margin: 0, fontSize: '18px', fontWeight: 700 }}>2%</p>
                </div>
                <div>
                    <p style={{ color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>Payouts</p>
                    <p style={{ color: 'var(--text)', margin: 0, fontSize: '18px', fontWeight: 700 }}>1st of each month</p>
                </div>
                <div>
                    <p style={{ color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>Min. payout</p>
                    <p style={{ color: 'var(--text)', margin: 0, fontSize: '18px', fontWeight: 700 }}>$10</p>
                </div>
            </div>

            {/* Referral code card */}
            <div style={{ ...cardStyle, marginBottom: '20px' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px' }}>Your Referral Link</p>
                {!code ? (
                    !hasChannels ? (
                        <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>
                            Connect a channel to get your referral code.
                        </p>
                    ) : (
                        <button onClick={generateCode} disabled={generating} style={{
                            background: 'var(--accent, #00AEEF)', color: '#fff', border: 'none', borderRadius: '8px',
                            padding: '10px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                            opacity: generating ? 0.6 : 1,
                        }}>
                            {generating ? 'Generating...' : 'Generate Referral Code'}
                        </button>
                    )
                ) : (
                    <>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            background: 'var(--surface-hover, rgba(0,0,0,0.04))',
                            border: '1px solid var(--border)', borderRadius: '8px',
                            padding: '10px 14px', marginBottom: '14px',
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                        }}>
                            <span style={{ flex: 1, fontSize: '13px', color: 'var(--text)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {refLink}
                            </span>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', background: 'rgba(0,174,239,0.1)', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                                {code}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={copyLink} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                background: copied ? '#10b981' : 'var(--accent)', color: '#fff',
                                fontSize: '13px', fontWeight: 600, transition: 'background 0.2s',
                                minHeight: '44px',
                            }}>
                                <Icons.Copy /> {copied ? 'Copied!' : 'Copy Link'}
                            </button>
                            <button onClick={shareTwitter} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border)', cursor: 'pointer',
                                background: 'transparent', color: 'var(--text)',
                                fontSize: '13px', fontWeight: 600,
                                minHeight: '44px',
                            }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.73-8.835L1.254 2.25H8.08l4.261 5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                Share on X
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: '14px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {statCard('Total Referred', stats?.total_referred ?? 0)}
                {statCard('Active Creators', stats?.active_creators ?? 0)}
                {statCard('This Month', stats?.earnings?.this_month != null ? `$${stats.earnings.this_month.toFixed(2)}` : '$0.00')}
                {statCard('All Time', stats?.earnings?.all_time != null ? `$${stats.earnings.all_time.toFixed(2)}` : '$0.00')}
            </div>

            {/* Earnings summary */}
            {stats && (
                <div style={{ ...cardStyle, marginBottom: '20px', display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>Pending Payout</p>
                        <p style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                            ${(stats?.earnings?.pending_payout ?? 0).toFixed(2)}
                        </p>
                    </div>
                    <div>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 4px', fontWeight: 500 }}>Last Month Earnings</p>
                        <p style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                            ${(stats?.earnings?.last_month ?? 0).toFixed(2)}
                        </p>
                    </div>
                </div>
            )}

            {/* Referred creators table */}
            <div style={{ ...cardStyle }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 16px' }}>
                    Referred Creators ({referred.length})
                </p>
                {referred.length === 0 ? (
                    <p style={{ fontSize: '14px', color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0', margin: 0 }}>
                        No referrals yet. Share your link to start earning!
                    </p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                    {['Username', 'Joined', 'Rate', 'Total Earned', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {referred.map((r, i) => {
                                    const rateLabel = r.boost_months_left != null && r.boost_months_left > 0
                                        ? `5% (${r.boost_months_left}mo left)`
                                        : '2% lifetime';
                                    const statusColor = r.status === 'active' ? '#10b981' : '#6b7280';
                                    return (
                                        <tr key={r.username ?? i} style={{ borderBottom: i < referred.length - 1 ? '1px solid var(--border)' : 'none' }}>
                                            <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 500 }}>@{r.username}</td>
                                            <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{r.joined_at ? new Date(r.joined_at).toLocaleDateString() : '—'}</td>
                                            <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{rateLabel}</td>
                                            <td style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 600 }}>${(r.total_earned ?? 0).toFixed(2)}</td>
                                            <td style={{ padding: '10px 12px' }}>
                                                <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: `${statusColor}20`, color: statusColor, textTransform: 'capitalize' }}>
                                                    {r.status ?? 'inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function DashboardPage() {
    const { user, logout, isSuperAdmin, isChatter, token } = useAuth();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const toast = useToast();
    const { confirm, confirmProps } = useConfirm();
    // If superadmin, show SuperAdmin dashboard
    if (isSuperAdmin) {
        return <SuperAdminDashboard token={token} logout={logout} />;
    }
    
    const [isImpersonated, setIsImpersonated] = useState(false);
    const [impersonatedBy, setImpersonatedBy] = useState('');

    const [wheels, setWheels] = useState([]);
    const [selectedWheel, setSelectedWheel] = useState(null);
    const [spinWheelEnabled, setSpinWheelEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('general');
    const [thumbnails, setThumbnails] = useState({});
    const [addingChannel, setAddingChannel] = useState(false);
    // showSettings is now derived from location.pathname
    const [adminDisplayName, setAdminDisplayName] = useState('');
    const [adminProfilePic, setAdminProfilePic] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [editingDisplayName, setEditingDisplayName] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [walletAddress, setWalletAddress] = useState('');
    // Chatter profile editing
    const [chatterProfile, setChatterProfile] = useState(null);
    const [editChatter, setEditChatter] = useState({});
    const showPayments = location.pathname === '/dashboard/payments';
    const showSettings = location.pathname === '/dashboard/settings';
    const showReferrals = location.pathname === '/dashboard/referrals';
    const [viewChatterEarnings, setViewChatterEarnings] = useState(null); // chatter object to view earnings for
    const [showAddChannelCode, setShowAddChannelCode] = useState(false); // chatter add channel modal
    const [addChannelTab, setAddChannelTab] = useState('code'); // 'code' or 'request'
    const [inviteCodeInput, setInviteCodeInput] = useState('');
    const [redeemingInvite, setRedeemingInvite] = useState(false);
    const [creatorUsernameInput, setCreatorUsernameInput] = useState('');
    const [sendingRequest, setSendingRequest] = useState(false);
    const [chatterRequests, setChatterRequests] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [prizeVaultPicker, setPrizeVaultPicker] = useState(null); // prize index or null
    const [deleteChannelConfirm, setDeleteChannelConfirm] = useState(null);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [prizeVaultItems, setPrizeVaultItems] = useState([]);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [telegramConnected, setTelegramConnected] = useState(null); // null = loading, false = not connected, true = connected
    const [connectCode, setConnectCode] = useState('');
    const [tgUsername, setTgUsername] = useState('');
    const [tgId, setTgId] = useState(null);
    const [tgBannerDismissed, setTgBannerDismissed] = useState(false);
    const [showTgConnect, setShowTgConnect] = useState(false);
    const [tgConnectCopied, setTgConnectCopied] = useState(false);
    const [tgConnectLoading, setTgConnectLoading] = useState(false);
    
    // Notifications state
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const seenNotificationIds = useRef(null); // null = not yet seeded
    
    // Support chat state
    const [showChat, setShowChat] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatUnread, setChatUnread] = useState(0);
    const [sendingChat, setSendingChat] = useState(false);
    const chatEndRef = useRef(null);

    // Creator-Chatter chat state
    const [showCCChat, setShowCCChat] = useState(false);
    const [ccChatWith, setCcChatWith] = useState(null);
    const [ccChatWithName, setCcChatWithName] = useState(null);
    const [ccChatMessages, setCcChatMessages] = useState([]);
    const [ccChatInput, setCcChatInput] = useState('');
    const [ccChatUnread, setCcChatUnread] = useState(0);
    const [ccConversations, setCcConversations] = useState([]);
    const [ccChatView, setCcChatView] = useState('list'); // 'list' | 'conversation'
    const [ccChatLoading, setCcChatLoading] = useState(false);
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [offerHourlyRate, setOfferHourlyRate] = useState('');
    const [offerPercentage, setOfferPercentage] = useState('');
    const [offerPricingType, setOfferPricingType] = useState('percentage');
    const [offerNote, setOfferNote] = useState('');
    const [offerChannels, setOfferChannels] = useState([]);
    const [acceptingOffer, setAcceptingOffer] = useState(null); // message being accepted (needs channel selection)
    const [acceptChannels, setAcceptChannels] = useState([]);

    // Mobile responsive
    const { isMobile } = useIsMobile();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Channel tab state (wheel, dms/chats, or vault) — chatters default to 'dms'
    const channelTab = showPayments || showSettings || showReferrals ? null : location.pathname === '/dashboard/guide' ? 'guide' : (location.pathname === '/dashboard/chatters' || location.pathname === '/marketplace') ? 'chatters' : location.pathname.includes('/dms') ? 'dms' : location.pathname.includes('/vault') ? 'vault' : location.pathname.includes('/subs') ? 'subs' : location.pathname.includes('/wheel') ? 'wheel' : (isChatter ? 'dms' : 'overview');
    const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
    const channelDropdownRef = useRef(null);
    const wheelCanvasRef = useRef(null);

    const defConfig = {
        title: '',
        banner_description: 'Spin to win exclusive content!',
        spin_button_text: 'Play now!',
        app_title: '',
        background_color_1: '#101923', background_color_2: '#101923', button_color: '#00E701', button_hover_color: '#00E701', text_color: '#ffffff',
        wheel_border_color: '#00E701',
        wheel_colors: ['#e495da', '#f240d1', '#e495da', '#f240d1', '#e495da', '#f240d1', '#e495da', '#f240d1', '#e495da', '#f240d1', '#e495da', '#f240d1'],
        prizes: Array(12).fill(null).map((_, i) => ({ index: i, label: `Prize ${i + 1}`, file_ids: [], types: [] })),
        buy_packages: [{ id: 1, spins: 1, price: 800, price_usd: 10, label: '1 Spin', popular: false }, { id: 2, spins: 5, price: 3100, price_usd: 40, label: '5 Spins (Save 20%)', popular: true }, { id: 3, spins: 10, price: 5400, price_usd: 70, label: '10 Spins (Save 35%)', popular: false }],
        banner_file_id: null, banner_type: 'photo',
        prize_count: 12
    };
    const [config, setConfig] = useState(defConfig);
    const [chancesEnabled, setChancesEnabled] = useState(false);

    // Sync route params to selected wheel
    useEffect(() => {
        if (params.channelId && params.channelId !== selectedWheel && wheels.length > 0) {
            const found = wheels.find(w => w.channel_username === params.channelId);
            if (found) handleSelectWheel(params.channelId);
        }
    }, [params.channelId, wheels]);

    // Close channel dropdown on click outside
    useEffect(() => {
        if (!channelDropdownOpen) return;
        const handler = (e) => {
            if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target)) {
                setChannelDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [channelDropdownOpen]);

    // Close sidebar on route change (mobile)
    useEffect(() => {
        if (isMobile) setSidebarOpen(false);
    }, [location.pathname]);

    // Fetch connect code when modal opens, then poll for connection
    const fetchConnectCode = async () => {
        setTgConnectLoading(true);
        try {
            const res = await api.post('/api/dashboard/account-info', { token: localStorage.getItem('token') });
            if (res.data.connect_code) setConnectCode(res.data.connect_code);
            if (res.data.telegram_linked) {
                setTelegramConnected(true);
                setShowTgConnect(false);
                setTgUsername(res.data.telegram_username || '');
                setTgId(res.data.telegram_id || null);
                loadWheels();
            }
        } catch (e) {
            console.error('Failed to fetch connect code:', e);
        } finally {
            setTgConnectLoading(false);
        }
    };
    useEffect(() => {
        if (!showTgConnect || isChatter) return;
        if (!connectCode) fetchConnectCode();
        // Poll every 3s to detect when user connects via Telegram
        const interval = setInterval(async () => {
            try {
                const res = await api.post('/api/dashboard/account-info', { token: localStorage.getItem('token') });
                if (res.data.telegram_linked) {
                    setTelegramConnected(true);
                    setShowTgConnect(false);
                    setTgUsername(res.data.telegram_username || '');
                    setTgId(res.data.telegram_id || null);
                    loadWheels();
                }
            } catch {}
        }, 3000);
        return () => clearInterval(interval);
    }, [showTgConnect, connectCode, isChatter]);

    useEffect(() => {
        // Check if current session is impersonated by superadmin
        const tkn = localStorage.getItem('token');
        if (tkn) {
            try {
                const payload = JSON.parse(atob(tkn.split('.')[1]));
                if (payload.impersonated_by) {
                    setIsImpersonated(true);
                    setImpersonatedBy(payload.impersonated_by);
                }
            } catch {}
        }

        loadWheels();
        if (!isChatter) {
            // Load notifications, chat unread, and pending requests in parallel (admin only)
            Promise.all([loadNotifications(), loadChatUnread(), loadPendingRequests()]);
            // Preload chat messages so they're ready when user opens chat
            loadChatMessages();
            // Poll notifications every 5 seconds
            const notifInterval = setInterval(loadNotifications, 5000);
            return () => clearInterval(notifInterval);
        } else {
            // Chatter notifications
            loadChatterNotifications();
            const notifInterval = setInterval(loadChatterNotifications, 5000);
            return () => clearInterval(notifInterval);
        }
    }, []);

    // KYC pending polling — after returning from Whop KYC onboarding flow
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('kyc_pending') !== 'true') return;

        toast.info('KYC submitted! Verification usually takes a few minutes.');
        window.history.replaceState({}, '', '/dashboard');

        let elapsed = 0;
        const interval = setInterval(async () => {
            elapsed += 10000;
            if (elapsed > 120000) {
                clearInterval(interval);
                return;
            }
            try {
                const res = await api.get('/api/dashboard/kyc-status');
                if (res.data.status === 'verified') {
                    clearInterval(interval);
                    toast.success('Identity verified!');
                }
            } catch {}
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    const loadNotifications = async () => {
        try {
            const res = await api.get('/api/dashboard/notifications');
            const notifs = res.data.notifications || [];
            setNotifications(notifs);
            setUnreadCount(res.data.unread_count || 0);
            if (seenNotificationIds.current === null) {
                // First load — seed without toasting
                seenNotificationIds.current = new Set(notifs.map(n => n.id));
            } else {
                const fresh = notifs.filter(n => !seenNotificationIds.current.has(n.id));
                if (fresh.length > 0) {
                    fresh.forEach(n => seenNotificationIds.current.add(n.id));
                    const first = fresh[0];
                    toast.success(fresh.length === 1 ? (first.title || 'New notification') : `${fresh.length} new notifications`);
                }
            }
        } catch (e) { /* silent */ }
    };
    
    const markNotificationRead = async (id) => {
        try {
            if (isChatter) {
                await api.post('/api/chatter/notifications/read', { notification_id: id });
                loadChatterNotifications();
            } else {
                await api.post('/api/dashboard/notifications/read', { notification_id: id });
                loadNotifications();
            }
        } catch (e) {}
    };
    
    const markAllRead = async () => {
        try {
            if (isChatter) {
                await api.post('/api/chatter/notifications/read', { notification_id: 'all' });
                loadChatterNotifications();
            } else {
                await api.post('/api/dashboard/notifications/read', { notification_id: 'all' });
                loadNotifications();
            }
        } catch (e) {}
    };

    const loadChatterNotifications = async () => {
        try {
            const res = await api.get('/api/chatter/notifications');
            const notifs = res.data.notifications || [];
            setNotifications(notifs);
            setUnreadCount(notifs.filter(n => !n.is_read).length);
            if (seenNotificationIds.current === null) {
                // First load — seed without toasting
                seenNotificationIds.current = new Set(notifs.map(n => n.id));
            } else {
                const fresh = notifs.filter(n => !seenNotificationIds.current.has(n.id));
                if (fresh.length > 0) {
                    fresh.forEach(n => seenNotificationIds.current.add(n.id));
                    const first = fresh[0];
                    toast.success(fresh.length === 1 ? (first.title || 'New notification') : `${fresh.length} new notifications`);
                }
            }
        } catch (e) { /* silent */ }
    };

    // Support chat functions
    const loadChatMessages = async () => {
        try {
            const res = await api.get('/api/chat/messages');
            setChatMessages(res.data.messages || []);
            setChatUnread(0);
        } catch (e) { /* silent */ }
    };
    
    const loadChatUnread = async () => {
        try {
            const res = await api.get('/api/chat/unread');
            setChatUnread(res.data.unread || 0);
        } catch (e) {}
    };
    
    const sendChatMessage = async (imageFile = null) => {
        if (!chatInput.trim() && !imageFile) return;
        if (sendingChat) return;
        
        const messageText = chatInput.trim();
        
        // Optimistic update - add message immediately
        const optimisticMsg = {
            id: 'temp-' + Date.now(),
            message: messageText,
            is_from_admin: false,
            created_at: new Date().toISOString(),
            sending: true
        };
        setChatMessages(prev => [...prev, optimisticMsg]);
        setChatInput('');
        
        setSendingChat(true);
        try {
            if (imageFile) {
                const fd = new FormData();
                fd.append('token', localStorage.getItem('token'));
                fd.append('message', messageText);
                fd.append('image', imageFile);
                await api.post('/api/chat/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await api.post('/api/chat/send', { message: messageText });
            }
            // Refresh to get server-confirmed message
            loadChatMessages();
        } catch (e) { 
            // Remove optimistic message on error
            setChatMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
            setChatInput(messageText); // Restore input
            toast.error(e.response?.data?.error || 'Failed to send'); 
        }
        finally { setSendingChat(false); }
    };
    
    const handleChatImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Image too large (max 10MB)');
                return;
            }
            sendChatMessage(file);
            e.target.value = '';
        }
    };
    
    useEffect(() => { 
        if (!showChat) {
            loadChatUnread(); 
            const interval = setInterval(loadChatUnread, 10000); 
            return () => clearInterval(interval); 
        }
    }, [showChat]);
    useEffect(() => { 
        if (showChat) {
            setChatUnread(0);  // Immediately clear badge
            loadChatMessages();  // This also marks as read on backend
            const interval = setInterval(loadChatMessages, 5000);
            return () => clearInterval(interval);
        }
    }, [showChat]);

    // Auto-scroll not needed with column-reverse

    useEffect(() => {
        if (!addingChannel) return;
        const interval = setInterval(async () => {
            try {
                const res = await api.get('/api/wheels');
                const newWheels = res.data.wheels || [];
                if (newWheels.length > wheels.length) {
                    setWheels(newWheels);
                    const newWheel = newWheels[newWheels.length - 1];
                    handleSelectWheel(newWheel.channel_username);
                    setAddingChannel(false);
                    setSuccess('Channel added!');
                    setTimeout(() => setSuccess(''), 3000);
                }
            } catch (e) {}
        }, 2000);
        return () => clearInterval(interval);
    }, [addingChannel, wheels.length]);

    const loadWheels = async () => {
        try {
            if (isChatter) {
                // Chatters don't have telegram connection — skip check
                setTelegramConnected(true);
                const [wheelsRes, profileRes] = await Promise.all([
                    api.get('/api/wheels'),
                    api.get('/api/chatter/profile').catch(() => null)
                ]);
                if (profileRes?.data?.profile) {
                    const p = profileRes.data.profile;
                    setAdminProfilePic(p.profile_picture || '');
                    setAdminDisplayName(p.display_name || '');
                    setChatterProfile(p);
                    setEditChatter(p);
                }
                const w = wheelsRes.data.wheels || [];
                setWheels(w);
                if (wheelsRes.data.spin_wheel_enabled !== undefined) setSpinWheelEnabled(wheelsRes.data.spin_wheel_enabled);
                if (w.length > 0 && !selectedWheel) {
                    const target = params.channelId || w[0].channel_username;
                    if (location.pathname === '/dashboard/payments' || location.pathname === '/dashboard' || location.pathname === '/dashboard/chatters' || location.pathname === '/marketplace') {
                        setSelectedWheel(target);
                    } else {
                        handleSelectWheel(target);
                    }
                }
            } else {
                // Load account info and wheels in parallel — catch each independently
                const [accountRes, wheelsRes] = await Promise.all([
                    api.post('/api/dashboard/account-info', { token: localStorage.getItem('token') }).catch(() => null),
                    api.get('/api/wheels').catch(() => null)
                ]);

                // Check telegram connection
                if (accountRes?.data) {
                    setTgUsername(accountRes.data.telegram_username || '');
                    setTgId(accountRes.data.telegram_id || null);
                    if (!accountRes.data.telegram_linked) {
                        setTelegramConnected(false);
                        if (accountRes.data.connect_code) {
                            setConnectCode(accountRes.data.connect_code);
                        }
                    } else {
                        setTelegramConnected(true);
                    }

                    // Check if chances feature is enabled (hidden)
                    if (accountRes.data.chances_enabled) {
                        setChancesEnabled(true);
                    }

                    // Load profile info
                    setAdminDisplayName(accountRes.data.display_name || '');
                    setAdminProfilePic(accountRes.data.profile_picture || '');
                    setAdminEmail(accountRes.data.email || '');
                    setWalletAddress(accountRes.data.wallet_address || '');
                } else {
                    // API failed — assume not connected so banner shows
                    setTelegramConnected(false);
                }

                // Set wheels
                const w = wheelsRes?.data?.wheels || [];
                setWheels(w);
                if (wheelsRes?.data?.spin_wheel_enabled !== undefined) setSpinWheelEnabled(wheelsRes.data.spin_wheel_enabled);
                if (w.length > 0 && !selectedWheel) {
                    const target = params.channelId || w[0].channel_username;
                    if (location.pathname === '/dashboard/payments' || location.pathname === '/dashboard' || location.pathname === '/dashboard/chatters' || location.pathname === '/marketplace') {
                        setSelectedWheel(target);
                    } else {
                        handleSelectWheel(target);
                    }
                }
            }
        } catch (e) {
            if (!isChatter) setTelegramConnected(prev => prev === null ? false : prev);
        } finally { setLoading(false); }
    };

    const handleSelectWheel = async (ch) => {
        setSelectedWheel(ch);
        // Navigate to channel route if not already there — chatters default to 'dms'
        const onChannel = location.pathname.includes('/dashboard/channel/');
        const currentTab = location.pathname.includes('/dms') ? 'dms' : location.pathname.includes('/vault') ? 'vault' : location.pathname.includes('/subs') ? 'subs' : (isChatter ? 'dms' : 'wheel');
        const targetPath = `/dashboard/channel/${ch}/${currentTab}`;
        if (location.pathname !== targetPath) {
            navigate(targetPath, { replace: onChannel });
        }
        try {
            const res = await api.get(`/api/wheels/${ch}`);
            const cfg = { ...defConfig, ...res.data, 
                prizes: (res.data.prizes || defConfig.prizes).map((p, i) => ({ ...defConfig.prizes[i], ...p })), 
                wheel_colors: res.data.wheel_colors || defConfig.wheel_colors,
                wheel_border_color: res.data.wheel_border_color || defConfig.wheel_border_color,
                button_hover_color: res.data.button_hover_color || defConfig.button_hover_color,
                buy_packages: res.data.buy_packages || defConfig.buy_packages,
                banner_description: res.data.banner_description || defConfig.banner_description,
                spin_button_text: res.data.spin_button_text || defConfig.spin_button_text,
                app_title: res.data.app_title || defConfig.app_title
            };
            setConfig(cfg);
            // Load thumbnails
            setThumbnails({});
            const allFileIds = [];
            cfg.prizes.forEach(p => (p.file_ids || []).forEach(f => allFileIds.push(f)));
            if (cfg.banner_file_id) allFileIds.push(cfg.banner_file_id);
            allFileIds.forEach(fid => loadThumb(fid));
        } catch (e) { setConfig(defConfig); }
    };

    const loadThumb = (fid) => {
        if (!fid) {
            return;
        }
        // Use proxy endpoint with thumb=1 for smaller/faster thumbnails
        const proxyUrl = `${API_URL}/api/dashboard/file?auth=${encodeURIComponent(localStorage.getItem('token')||'')}&file_id=${encodeURIComponent(fid)}&thumb=1`;
        setThumbnails(p => ({ ...p, [fid]: proxyUrl }));
    };

    const requestAddChannel = async () => {
        setError(''); setAddingChannel(true);
        try {
            await api.post('/api/dashboard/request-add-channel', { token: localStorage.getItem('token') });
            setSuccess('📱 Check your Telegram! Click the button there to select a channel.');
            setTimeout(() => setSuccess(''), 8000);
            // Poll for new channels every 2s for up to 60s
            const prevNames = wheels.map(w => w.channel_username);
            let attempts = 0;
            const poll = setInterval(async () => {
                attempts++;
                try {
                    const res = await api.get('/api/wheels');
                    const w = res.data.wheels || [];
                    const newWheel = w.find(x => !prevNames.includes(x.channel_username));
                    if (newWheel) {
                        clearInterval(poll);
                        setWheels(w);
                        setAddingChannel(false);
                        handleSelectWheel(newWheel.channel_username);
                        setSuccess('Channel added successfully!');
                        setTimeout(() => setSuccess(''), 3000);
                    }
                } catch (e) { /* ignore poll errors */ }
                if (attempts >= 30) { clearInterval(poll); setAddingChannel(false); }
            }, 2000);
        } catch (e) { setError(e.response?.data?.error || 'Failed'); setAddingChannel(false); }
    };

    const deleteWheel = async (ch) => {
        try {
            await api.post('/api/wheels/delete', { channel_username: ch, token: localStorage.getItem('token') });
            const remaining = wheels.filter(x => x.channel_username !== ch);
            setWheels(remaining);
            if (selectedWheel === ch) {
                if (remaining.length > 0) {
                    handleSelectWheel(remaining[0].channel_username);
                } else {
                    setSelectedWheel(null);
                    setConfig(defConfig);
                }
            }
            setDeleteChannelConfirm(null);
            setSuccess('Channel deleted!'); setTimeout(() => setSuccess(''), 2000);
        } catch (e) { setError(e.response?.data?.error || 'Failed to delete'); setDeleteChannelConfirm(null); }
    };

    const saveConfig = async () => {
        if (!selectedWheel) return; 
        setSaving(true);
        try {
            // Validate and normalize prize chances before saving (if chancesEnabled)
            let configToSave = { ...config };
            if (chancesEnabled) {
                const prizeCount = config.prize_count || 12;
                const prizes = [...config.prizes];
                const customPrizes = prizes.slice(0, prizeCount).filter(p => p.chance !== undefined);
                
                if (customPrizes.length > 0) {
                    const customTotal = customPrizes.reduce((sum, p) => sum + (p.chance || 0), 0);
                    
                    // Validate: custom chances
                    if (customTotal > 100) {
                        toast.error(`Custom chances total ${customTotal.toFixed(1)}% - cannot exceed 100%`);
                        setSaving(false);
                        return;
                    }
                    
                    // If not all prizes have custom values, total must be less than 100 to leave room for auto-fill
                    const autoPrizeCount = prizeCount - customPrizes.length;
                    if (autoPrizeCount > 0 && customTotal >= 100) {
                        toast.error(`Custom chances total ${customTotal.toFixed(1)}% - leaves no room for other ${autoPrizeCount} prizes`);
                        setSaving(false);
                        return;
                    }
                    
                    // Validate: each chance must be positive
                    if (customPrizes.some(p => p.chance <= 0)) {
                        toast.error('Each custom chance must be greater than 0%');
                        setSaving(false);
                        return;
                    }
                    
                    // Calculate remaining for auto-prizes
                    const remaining = 100 - customTotal;
                    const autoCount = prizeCount - customPrizes.length;
                    const autoChance = autoCount > 0 ? remaining / autoCount : 0;
                    
                    // Set auto-calculated chances for non-custom prizes
                    for (let i = 0; i < prizeCount; i++) {
                        if (prizes[i].chance === undefined) {
                            prizes[i] = { ...prizes[i], chance: parseFloat(autoChance.toFixed(2)) };
                        }
                    }
                    
                    configToSave.prizes = prizes;
                }
            }
            
            await api.post('/api/wheels/save', { ...configToSave, channel_username: selectedWheel, background_gradient: `linear-gradient(135deg, ${config.background_color_1} 0%, ${config.background_color_2} 100%)`, token: localStorage.getItem('token') }); 
            toast.success('Changes saved!'); 
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); } 
        finally { setSaving(false); }
    };

    const uploadFile = async (file, type, pi = null) => {
        // Frontend file size validation
        const maxImageSize = 10 * 1024 * 1024; // 10MB
        const maxVideoSize = 100 * 1024 * 1024; // 100MB
        const isVideo = file.type.startsWith('video/') || file.name.toLowerCase().match(/\.(mp4|mov|avi|webm)$/);
        const isImage = file.type.startsWith('image/') || file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
        
        if (isImage && file.size > maxImageSize) {
            toast.error(`Image too large. Max 10MB, yours is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
            return;
        }
        
        if (isVideo && file.size > maxVideoSize) {
            toast.error(`Video too large. Max 100MB, yours is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
            return;
        }
        
        if (type === 'banner' && isVideo) {
            toast.error('Videos not allowed for banners. Use JPG or PNG.');
            return;
        }
        
        const fd = new FormData(); 
        fd.append('file', file); 
        fd.append('token', localStorage.getItem('token'));
        fd.append('type', type); // Pass type for banner restrictions
        try {
            toast.info('Uploading...');
            const res = await api.post('/api/dashboard/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const newFileId = res.data.file_id;
            
            if (type === 'banner') {
                
                setConfig(p => ({ ...p, banner_file_id: newFileId, banner_type: res.data.type }));
            } else if (type === 'prize' && pi !== null) { 
                const np = [...config.prizes]; 
                np[pi].file_ids = [...(np[pi].file_ids || []), newFileId]; 
                np[pi].types = [...(np[pi].types || []), res.data.type]; 
                setConfig(p => ({ ...p, prizes: np })); 
            }
            // Load thumbnail for the new file
            loadThumb(newFileId);
            if (res.data.duplicate) {
                toast.success('File already exists, using existing copy');
            } else {
                toast.success('Uploaded successfully');
            }
        } catch (e) { 
            console.error('Upload error:', e);
            toast.error(e.response?.data?.error || 'Upload failed'); 
        }
    };

    const rmImg = async (pi, ii) => { 
        const np = [...config.prizes]; 
        const fileIdToDelete = np[pi].file_ids[ii];
        np[pi].file_ids = np[pi].file_ids.filter((_, i) => i !== ii); 
        np[pi].types = np[pi].types.filter((_, i) => i !== ii); 
        setConfig(p => ({ ...p, prizes: np })); 
        
        // Delete file from server
        if (fileIdToDelete) {
            try {
                const res = await api.post('/api/dashboard/delete-file', { 
                    token: localStorage.getItem('token'), 
                    file_id: fileIdToDelete 
                });
                if (res.data.success) {
                    setSuccess('File deleted!');
                    setTimeout(() => setSuccess(''), 1500);
                }
            } catch (e) {
                console.error('Failed to delete file:', e);
                setError('Failed to delete file from server');
            }
        }
    };

    const rmBanner = async () => {
        const fileIdToDelete = config.banner_file_id;
        setConfig(p => ({ ...p, banner_file_id: null, banner_type: 'photo' }));
        
        // Delete file from server
        if (fileIdToDelete) {
            try {
                const res = await api.post('/api/dashboard/delete-file', { 
                    token: localStorage.getItem('token'), 
                    file_id: fileIdToDelete 
                });
                if (res.data.success) {
                    setSuccess('Banner deleted!');
                    setTimeout(() => setSuccess(''), 1500);
                }
            } catch (e) {
                console.error('Failed to delete banner:', e);
            }
        }
    };

    const postChannel = async () => { 
        try { 
            toast.info('Posting...');
            await api.post('/api/dashboard/post-to-channel', { token: localStorage.getItem('token'), channel_username: selectedWheel }); 
            toast.success('Posted to channel!'); 
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to post'); } 
    };

    const changePassword = async () => {
        if (!currentPassword) { toast.error('Current password is required'); return; }
        if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
        try {
            await api.post('/api/dashboard/change-password', { current_password: currentPassword, new_password: newPassword });
            toast.success('Password changed!'); setNewPassword(''); setConfirmPassword(''); setCurrentPassword('');
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to change password'); }
    };
    

    const deepLink = selectedWheel ? `https://t.me/${BOT_USERNAME}?start=play_${selectedWheel}` : '';

    const redeemInviteCode = async () => {
        if (!inviteCodeInput.trim()) { toast.error('Please enter an invite code'); return; }
        setRedeemingInvite(true);
        try {
            const res = await api.post('/api/chatter/redeem-code', { code: inviteCodeInput.trim() });
            toast.success('Channel access granted!');
            setInviteCodeInput('');
            setShowAddChannelCode(false);
            if (res.data.token) {
                localStorage.setItem('token', res.data.token);
            }
            loadWheels();
        } catch (e) { toast.error(e.response?.data?.error || 'Invalid or expired code'); }
        finally { setRedeemingInvite(false); }
    };

    const loadChatterRequests = async () => {
        try {
            const res = await api.get('/api/chatter/request-status');
            setChatterRequests(res.data.requests || []);
        } catch (e) { /* silent */ }
    };

    const sendConnectRequest = async () => {
        const username = creatorUsernameInput.trim().replace('@', '').toLowerCase();
        if (!username) { toast.error('Please enter a creator username'); return; }
        setSendingRequest(true);
        try {
            await api.post('/api/chatter/connect-request', { creator_username: username });
            toast.success('Request sent! The creator will review your request.');
            setCreatorUsernameInput('');
            loadChatterRequests();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to send request'); }
        finally { setSendingRequest(false); }
    };

    // ── Pending Requests & Browse Chatters ──
    const loadPendingRequests = async () => {
        try {
            const res = await api.get('/api/admin/chatters/requests');
            setPendingRequests(res.data.requests || []);
        } catch (e) { /* silent */ }
    };

    // ===== Creator-Chatter Chat Functions =====
    const loadCCConversations = async () => {
        try {
            const res = await api.get('/api/cc-chat/conversations');
            setCcConversations(res.data.conversations || []);
        } catch (e) {}
    };

    const loadCCUnread = async () => {
        try {
            const res = await api.get('/api/cc-chat/unread');
            setCcChatUnread(res.data.unread || 0);
        } catch (e) {}
    };

    const loadCCMessages = useCallback(async (withUser, showLoading = false) => {
        try {
            if (showLoading) setCcChatLoading(true);
            const res = await api.get(`/api/cc-chat/messages?with=${withUser}`);
            setCcChatMessages(res.data.messages || []);
        } catch (e) {}
        finally { if (showLoading) setCcChatLoading(false); }
    }, []);

    const startCCChat = async (withUsername, displayName) => {
        try {
            await api.post('/api/cc-chat/start', { with: withUsername });
            setCcChatWith(withUsername);
            setCcChatWithName(displayName || withUsername);
            setCcChatView('conversation');
            setShowCCChat(true);
            loadCCMessages(withUsername);
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to start chat'); }
    };

    const [ccSending, setCcSending] = useState(false);
    const sendCCMessage = async () => {
        if (!ccChatInput.trim() || !ccChatWith || ccSending) return;
        const msg = ccChatInput.trim();
        setCcChatInput('');
        setCcSending(true);
        try {
            await api.post('/api/cc-chat/send', { to: ccChatWith, message: msg });
            loadCCMessages(ccChatWith);
        } catch (e) {
            if (e.response?.status === 429) toast.error('Slow down — wait a moment');
            else toast.error('Failed to send');
        }
        finally { setTimeout(() => setCcSending(false), 1000); }
    };

    const sendOffer = async () => {
        try {
            await api.post('/api/cc-chat/send-offer', {
                to: ccChatWith,
                hourly_rate: offerHourlyRate || null,
                percentage: offerPercentage || null,
                pricing_type: offerPricingType,
                message: offerNote,
                channels: offerChannels,
            });
            setShowOfferModal(false);
            setOfferHourlyRate(''); setOfferPercentage(''); setOfferNote(''); setOfferChannels([]);
            loadCCMessages(ccChatWith);
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to send offer'); }
    };

    const respondToOffer = async (messageId, action, channels = []) => {
        try {
            await api.post('/api/cc-chat/respond-offer', { message_id: messageId, action, channels });
            setAcceptingOffer(null);
            setAcceptChannels([]);
            loadCCMessages(ccChatWith);
            if (action === 'accept') {
                toast.success('Offer accepted! Agreement is now active.');
                loadWheels(); // Reload channels since assignment changed
            }
        } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    };

    const openCCChatList = () => {
        if (showCCChat) { setShowCCChat(false); return; }
        setCcChatView('list');
        setCcChatWith(null);
        setShowCCChat(true);
        loadCCConversations();
    };

    // CC Chat polling
    useEffect(() => {
        loadCCUnread();
        const interval = setInterval(loadCCUnread, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (showCCChat && ccChatWith && ccChatView === 'conversation') {
            loadCCMessages(ccChatWith, true);
            const interval = setInterval(() => loadCCMessages(ccChatWith), 5000);
            return () => clearInterval(interval);
        }
    }, [showCCChat, ccChatWith, ccChatView, loadCCMessages]);

    // Draw wheel preview canvas
    useEffect(() => {
        const canvas = wheelCanvasRef.current;
        if (!canvas || !config) return;
        const ctx = canvas.getContext('2d');
        const size = canvas.width;
        const center = size / 2;
        const radius = size / 2;
        const count = config.prize_count || 12;
        const segAngle = (2 * Math.PI) / count;
        const colors = config.wheel_colors || [];
        const prizes = config.prizes || [];

        ctx.clearRect(0, 0, size, size);
        ctx.save();
        ctx.translate(center, center);

        for (let i = 0; i < count; i++) {
            const startAngle = i * segAngle - Math.PI / 2;
            const endAngle = startAngle + segAngle;

            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.arc(0, 0, radius, startAngle, endAngle);
            ctx.closePath();
            ctx.fillStyle = colors[i % colors.length] || '#e495da';
            ctx.fill();
            ctx.strokeStyle = 'rgba(15, 25, 35, 0.6)';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Label
            ctx.save();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            const midAngle = startAngle + segAngle / 2;
            ctx.rotate(midAngle);
            ctx.translate(radius - 10, 0);
            ctx.rotate(Math.PI);

            let label = (prizes[i] && prizes[i].label) || `Prize ${i + 1}`;
            if (label.length > 14) label = label.substring(0, 13) + '\u2026';

            let fontSize = 10;
            if (label.length > 12) fontSize = 7;
            else if (label.length > 9) fontSize = 8;
            else if (label.length > 6) fontSize = 9;

            ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
            ctx.fillStyle = '#fff';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.shadowColor = 'rgba(0,0,0,0.7)';
            ctx.shadowBlur = 3;
            ctx.fillText(label, 0, 0);
            ctx.restore();
        }

        ctx.restore();
    }, [config?.wheel_colors, config?.prizes, config?.prize_count, config?.background_color_1, config?.button_color, config?.wheel_border_color, selectedWheel, channelTab, activeTab]);

    const openPrizeVaultPicker = async (prizeIndex) => {
        setPrizeVaultPicker(prizeIndex);
        try {
            const res = await api.get(`/api/vault/list?channel=${selectedWheel || ''}&per_page=100`);
            setPrizeVaultItems(res.data.items || []);
        } catch (err) { console.error('Vault load error:', err); }
    };

    const attachVaultToPrize = (item) => {
        if (prizeVaultPicker === null) return;
        const np = [...config.prizes];
        np[prizeVaultPicker].file_ids = [...(np[prizeVaultPicker].file_ids || []), item.file_id];
        np[prizeVaultPicker].types = [...(np[prizeVaultPicker].types || []), item.file_type];
        setConfig(p => ({ ...p, prizes: np }));
        loadThumb(item.file_id);
        setPrizeVaultPicker(null);
        toast.success('Added from vault');
    };

    if (loading) return <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={'/logo-light.webp'} alt="Loading" style={{ height: '40px', opacity: 0.6, animation: 'pulse 1.5s ease-in-out infinite' }} /></div>;

    // telegramConnected === null just means still loading, handled by loading check above

    // Helper: get selected wheel object
    const selectedWheelObj = wheels.find(w => w.channel_username === selectedWheel);

    // Helper: switch channel tab
    const switchTab = (tab) => {
        if (selectedWheel) {
            navigate(`/dashboard/channel/${selectedWheel}/${tab}`);
        }
    };

    return (
        <div style={{ height: '100vh', fontFamily: fontStack, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            {isImpersonated && (
                <div style={{ background: '#ef4444', color: 'white', padding: '8px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, position: 'sticky', top: 0, zIndex: 9999, flexShrink: 0 }}>
                    Viewing as @{user?.username} — Superadmin mode ({impersonatedBy})
                </div>
            )}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
            {/* Mobile sidebar backdrop */}
            {isMobile && sidebarOpen && (
                <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
            )}
            {/* ═══ LEFT SIDEBAR ═══ */}
            <aside style={{
                width: '260px', flexShrink: 0, background: 'var(--sidebar-bg)',
                backdropFilter: 'var(--glass-blur)', WebkitBackdropFilter: 'var(--glass-blur)',
                borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                ...(isMobile ? {
                    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1000,
                    transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.25s ease',
                    boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.15)' : 'none',
                } : {})
            }}>
                {/* Logo */}
                <div style={{ padding: '20px 20px 8px', flexShrink: 0 }}>
                    <img src={'/logo-light.webp'} alt="Privly" style={{ height: '38px', width: 'auto', objectFit: 'contain' }} />
                </div>

                {/* Navigation Items */}
                <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 12px 16px', display: 'flex', flexDirection: 'column' }}>

                    {/* Channel Selector (dropdown) */}
                    <div style={{ marginBottom: '4px', position: 'relative' }} ref={channelDropdownRef}>
                        <div onClick={() => setChannelDropdownOpen(p => !p)} style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                            background: 'var(--surface-hover)', border: '1px solid var(--border)',
                            borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
                            ...(channelDropdownOpen ? { borderColor: 'var(--accent)' } : {})
                        }}>
                            {selectedWheelObj ? (
                                <>
                                    <ChannelAvatar username={selectedWheelObj.channel_username} title={selectedWheelObj.title} size={28} radius={7} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedWheelObj.title || 'Channel'}</p>
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>@{selectedWheelObj.channel_username}</p>
                                    </div>
                                </>
                            ) : (
                                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>Select Channel</span>
                            )}
                            <Icons.ChevronDown />
                        </div>

                        {channelDropdownOpen && (
                            <div style={{
                                position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', zIndex: 100,
                                background: 'rgba(255,255,255,0.98)',
                                border: '1px solid var(--border)', borderRadius: '10px',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.25)', backdropFilter: 'blur(12px)',
                                maxHeight: '260px', overflowY: 'auto'
                            }}>
                                {wheels.map(w => (
                                    <div key={w.channel_username} onClick={() => { handleSelectWheel(w.channel_username); setChannelDropdownOpen(false); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                                            cursor: 'pointer', transition: 'background 0.1s',
                                            background: selectedWheel === w.channel_username ? 'var(--surface-hover)' : 'transparent'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseLeave={e => { if (selectedWheel !== w.channel_username) e.currentTarget.style.background = 'transparent'; }}>
                                        <ChannelAvatar username={w.channel_username} title={w.title} size={26} radius={6} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.title || 'Channel'}</p>
                                            <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: 0 }}>@{w.channel_username}</p>
                                        </div>
                                        {selectedWheel === w.channel_username && <Icons.Check />}
                                        {!isChatter && (
                                            <div onClick={e => { e.stopPropagation(); setDeleteChannelConfirm(w.channel_username); setChannelDropdownOpen(false); }}
                                                style={{ padding: '4px', borderRadius: '6px', color: 'var(--text-muted)', opacity: 0.5, transition: 'all 0.15s', flexShrink: 0 }}
                                                onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.opacity = '1'; }}
                                                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.opacity = '0.5'; }}>
                                                <Icons.Trash />
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {isChatter ? (
                                    <div onClick={() => { setAddChannelTab('code'); loadChatterRequests(); setShowAddChannelCode(true); setChannelDropdownOpen(false); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderTop: '1px solid var(--border)', color: 'var(--accent)', fontSize: '12px', fontWeight: 600 }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <Icons.Plus /> Add Channel
                                    </div>
                                ) : (
                                    <div onClick={() => { requestAddChannel(); setChannelDropdownOpen(false); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', borderTop: '1px solid var(--border)', color: 'var(--accent)', fontSize: '12px', fontWeight: 600 }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <Icons.Plus /> Add Channel
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar nav item helper */}
                    {(() => {
                        const sLabel = (text) => (
                            <p style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '0 4px', margin: '24px 0 2px' }}>{text}</p>
                        );
                        const sItem = ({ label, icon: Icon, active, badge, badgeColor, onClick }) => (
                            <div onClick={onClick}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 8px',
                                    borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: active ? 600 : 400,
                                    color: 'var(--text)',
                                    background: 'transparent',
                                    transition: 'background 0.15s ease',
                                }}>
                                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: 0.7 }}><Icon /></span>
                                <span style={{ flex: 1 }}>{label}</span>
                                {badge > 0 && <span style={{ minWidth: '18px', height: '18px', borderRadius: '9px', background: badgeColor || '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{badge}</span>}
                            </div>
                        );

                        return (
                            <>
                                {/* Home section */}
                                {!isChatter && sLabel('Home')}
                                {!isChatter && sItem({ label: 'Overview', icon: Icons.ChartBar, active: channelTab === 'overview', onClick: () => navigate('/dashboard') })}
                                {!isChatter && sItem({ label: 'Marketplace', icon: Icons.Users, active: channelTab === 'chatters', badge: pendingRequests.length, badgeColor: '#f59e0b', onClick: () => navigate('/marketplace') })}

                                {/* Channel section */}
                                {selectedWheel && (
                                    <>
                                        {sLabel('Channel')}
                                        {sItem({ label: 'Direct Messages', icon: Icons.MessageCircle, active: channelTab === 'dms', onClick: () => switchTab('dms') })}
                                        {sItem({ label: 'Vault', icon: Icons.Lock, active: channelTab === 'vault', onClick: () => switchTab('vault') })}
                                        {!isChatter && sItem({ label: 'Subscriptions', icon: Icons.DollarSign, active: channelTab === 'subs', onClick: () => switchTab('subs') })}
                                        {!isChatter && sItem({ label: <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Spin{!spinWheelEnabled && <span style={{ background: '#f0c040', color: '#000', padding: '1px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 600, lineHeight: '14px' }}>SOON</span>}</span>, icon: Icons.Grid, active: channelTab === 'wheel', onClick: () => switchTab('wheel') })}
                                    </>
                                )}

                                {/* Profile section */}
                                {sLabel('Profile')}
                                {sItem({ label: 'Guide', icon: Icons.Book, active: channelTab === 'guide', onClick: () => navigate('/dashboard/guide') })}
                                {sItem({ label: 'Notifications', icon: Icons.Bell, active: false, badge: unreadCount, badgeColor: '#ef4444', onClick: () => { setShowNotifications(true); setUnreadCount(0); markAllRead(); } })}
                                {sItem({ label: 'Messages', icon: Icons.MessageCircle, active: false, badge: ccChatUnread, badgeColor: '#00AEEF', onClick: openCCChatList })}
                                {sItem({ label: 'Earnings', icon: Icons.Wallet, active: showPayments, onClick: () => navigate('/dashboard/payments') })}
                                {!isChatter && sItem({ label: <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Referrals<span style={{ background: '#16a34a', color: '#fff', padding: '1px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 600, lineHeight: '14px' }}>NEW</span></span>, icon: Icons.Link, active: showReferrals, onClick: () => navigate('/dashboard/referrals') })}
                                {sItem({ label: 'Settings', icon: Icons.Settings, active: showSettings, onClick: async () => { navigate('/dashboard/settings'); setEditingDisplayName(adminDisplayName); if (isChatter && !chatterProfile) { try { const res = await api.get('/api/chatter/profile'); setChatterProfile(res.data.profile); setEditChatter(res.data.profile); } catch {} } } })}
                            </>
                        );
                    })()}
                </nav>

                {/* Bottom section */}
                <div style={{ padding: '12px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 8px', borderRadius: '7px', cursor: 'default', fontSize: '13px', fontWeight: 400, color: 'var(--text)', background: 'transparent' }}>
                        {adminProfilePic ? (
                            <img src={`${API_URL}/api/profile/picture?username=${user?.username}`} alt="" style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                                {(user?.username || '?')[0].toUpperCase()}
                            </div>
                        )}
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminDisplayName || `@${user?.username}`}</span>
                            <span style={{ display: 'inline-block', fontSize: '9px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', padding: '1px 6px', borderRadius: '4px', marginTop: '2px', background: isChatter ? 'rgba(139,92,246,0.15)' : 'rgba(0,174,239,0.12)', color: isChatter ? '#8b5cf6' : 'var(--accent)', border: `1px solid ${isChatter ? 'rgba(139,92,246,0.3)' : 'rgba(0,174,239,0.25)'}` }}>{isChatter ? 'Chatter' : 'Creator'}</span>
                        </span>
                    </div>
                    <div onClick={() => { logout(); navigate('/login'); }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 8px', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: 400, color: 'var(--text)', background: 'transparent', transition: 'background 0.15s ease' }}>
                        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, opacity: 0.7 }}><Icons.LogOut /></span>
                        Logout
                    </div>
                </div>
            </aside>

            {/* ═══ MAIN AREA ═══ */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, background: 'var(--bg-alt, #f8fafc)' }}>

            {/* Mobile top bar */}
            {isMobile && (
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid var(--border)', background: 'var(--sidebar-bg)', flexShrink: 0 }}>
                    <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text)', display: 'flex' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                    </button>
                    <img src="/logo-light.webp" alt="Privly" style={{ height: '26px' }} />
                </div>
            )}

            {/* Telegram not connected warning banner */}
            {!isChatter && telegramConnected === false && !tgBannerDismissed && (
                <div style={{ padding: '12px 16px', background: '#fef3c7', borderBottom: '2px solid #f59e0b', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <span style={{ flex: 1, fontSize: '13px', color: '#92400e', fontWeight: 500 }}>
                        Connect your Telegram to start using the platform.
                    </span>
                    <button onClick={() => setShowTgConnect(true)}
                        style={{ fontSize: '12px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', padding: '6px 14px', border: 'none', borderRadius: '6px', background: '#f59e0b', cursor: 'pointer', fontFamily: fontStack }}>
                        Connect
                    </button>
                    <button onClick={() => setTgBannerDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', padding: '2px', display: 'flex' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            )}

            {/* Floating Support Chat Bubble — hidden on DMs tab */}
            {!showChat && channelTab !== 'dms' && (
                <button
                    onClick={() => { setShowChat(true); setChatUnread(0); }}
                    style={{
                        position: 'fixed', bottom: isMobile ? '16px' : '24px', right: isMobile ? '16px' : '24px', zIndex: 1000,
                        width: isMobile ? '48px' : '56px', height: isMobile ? '48px' : '56px', borderRadius: '50%', border: 'none',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        color: '#fff', cursor: 'pointer',
                        boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(99,102,241,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.4)'; }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    {chatUnread > 0 && (
                        <span style={{
                            position: 'absolute', top: '-4px', right: '-4px',
                            minWidth: '20px', height: '20px', borderRadius: '10px',
                            background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: '0 5px', border: '2px solid #fff'
                        }}>
                            {chatUnread}
                        </span>
                    )}
                </button>
            )}

            {/* Support Chat Modal */}
            {showChat && (
                <div style={{ position: 'fixed', ...(isMobile ? { inset: 0, borderRadius: 0, width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' } : { bottom: '24px', right: '24px', width: '380px', maxWidth: 'calc(100vw - 40px)', height: '500px', maxHeight: 'calc(100vh - 60px)', borderRadius: '16px' }), background: 'var(--card-bg)', border: isMobile ? 'none' : '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 1001, overflow: 'hidden' }}>
                    {/* Chat Header */}
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', boxSizing: 'border-box' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"></line>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '14px' }}>Support</div>
                                <div style={{ fontSize: '11px', color: '#10b981' }}>Online</div>
                            </div>
                        </div>
                        <button onClick={() => setShowChat(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>×</button>
                    </div>
                    
                    {/* Messages */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column-reverse', gap: '12px' }}>
                        {chatMessages.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                                    <Icons.Send />
                                </div>
                                <p style={{ marginTop: '12px', fontSize: '13px' }}>How can we help you?</p>
                            </div>
                        ) : (
                            [...chatMessages].reverse().map(m => (
                                <div key={m.id} style={{ display: 'flex', justifyContent: m.is_from_admin ? 'flex-start' : 'flex-end' }}>
                                    <div style={{ 
                                        maxWidth: '80%', 
                                        padding: m.image_file_id ? '6px' : '10px 14px', 
                                        borderRadius: m.is_from_admin ? '12px 12px 12px 4px' : '12px 12px 4px 12px',
                                        background: m.is_from_admin ? 'rgba(99, 102, 241, 0.2)' : 'var(--surface-hover)',
                                        border: m.is_from_admin ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid var(--border)',
                                        color: 'var(--text)',
                                        fontSize: '13px'
                                    }}>
                                        {m.image_file_id && (
                                            <img 
                                                src={`${API_URL}/api/chat/image?id=${m.image_file_id}`} 
                                                alt="Attachment" 
                                                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', display: 'block', marginBottom: m.message ? '8px' : '0', cursor: 'pointer' }}
                                                onClick={() => window.open(`${API_URL}/api/chat/image?id=${m.image_file_id}`, '_blank')}
                                            />
                                        )}
                                        {m.message && <div style={{ padding: m.image_file_id ? '4px 8px' : 0 }}>{m.message}</div>}
                                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', padding: m.image_file_id ? '0 8px 4px' : 0 }}>
                                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input 
                            type="file"
                            accept="image/*"
                            onChange={handleChatImageSelect}
                            style={{ display: 'none' }}
                            id="chat-image-input"
                        />
                        <label 
                            htmlFor="chat-image-input"
                            style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--surface-hover)', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                        </label>
                        <input 
                            type="text"
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
                            placeholder="Type a message..."
                            style={{ flex: 1, padding: '10px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '20px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, outline: 'none' }}
                        />
                        <button 
                            onClick={() => sendChatMessage()} 
                            disabled={sendingChat || !chatInput.trim()}
                            style={{ width: '38px', height: '38px', borderRadius: '50%', background: chatInput.trim() ? '#6366f1' : 'var(--surface-hover)', border: 'none', color: chatInput.trim() ? '#fff' : 'var(--text-muted)', cursor: chatInput.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Notifications Modal */}
            {showNotifications && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={() => { markAllRead(); setShowNotifications(false); }}>
                    <div style={{ background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)', border: '1px solid var(--glass-border)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', padding: '30px 35px', width: '100%', maxWidth: '450px', margin: '20px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icons.Bell />
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Notifications</h2>
                                {unreadCount > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 600 }}>{unreadCount} new</span>}
                            </div>
                            <button onClick={() => { markAllRead(); setShowNotifications(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                        
                        {unreadCount > 0 && (
                            <button onClick={markAllRead} style={{ ...btnSmall, marginBottom: '16px', alignSelf: 'flex-start' }}>Mark all as read</button>
                        )}
                        
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {notifications.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                    <Icons.Bell />
                                    <p style={{ marginTop: '12px' }}>No notifications yet</p>
                                </div>
                            ) : (
                                notifications.map(n => {
                                    const notifIcon = {
                                        tip: <Icons.DollarSign />,
                                        ppv_sale: <Icons.Gift />,
                                        sale: <Icons.Gift />,
                                        welcome: <Icons.Award />,
                                        payout_requested: <Icons.DollarSign />,
                                        payout_approved: <Icons.Check />,
                                        payout_rejected: <Icons.X />,
                                        subscription_activated: <Icons.Users />,
                                        subscription_payment: <Icons.DollarSign />,
                                        subscription_payment_failed: <Icons.Bell />,
                                        subscription_canceled: <Icons.X />,
                                        subscription_chargeback: <Icons.Bell />,
                                        subscription_refund: <Icons.DollarSign />,
                                        chatter_request: <Icons.Users />,
                                        cc_message: <Icons.MessageCircle />,
                                        cc_offer: <Icons.DollarSign />,
                                        cc_offer_rejected: <Icons.X />,
                                        cc_offer_accepted: <Icons.Check />,
                                        fraud_detected: <Icons.Bell />,
                                    }[n.notification_type || n.type] || <Icons.Bell />;
                                    const handleNotifClick = () => {
                                        if (!n.is_read) markNotificationRead(n.id);
                                        const type = n.notification_type || n.type;
                                        if (type === 'chatter_request') {
                                            setShowNotifications(false);
                                            navigate('/marketplace');
                                        }
                                    };
                                    return (
                                    <div key={n.id} onClick={handleNotifClick} style={{ padding: '14px', background: n.is_read ? 'transparent' : 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ color: 'var(--text-secondary)', display: 'flex', flexShrink: 0 }}>{notifIcon}</span>
                                                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{n.title}</h4>
                                            </div>
                                            {!n.is_read && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--text)', flexShrink: 0, marginTop: '4px' }}></span>}
                                        </div>
                                        <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-muted)' }}>{n.message}</p>
                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString()}</span>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Creator-Chatter Chat Modal */}
            {showCCChat && (
                <div style={{ position: 'fixed', ...(isMobile ? { inset: 0, borderRadius: 0, width: '100%', height: '100%' } : { bottom: '24px', right: isMobile ? '16px' : '100px', width: '420px', maxWidth: 'calc(100vw - 40px)', height: '560px', maxHeight: 'calc(100vh - 60px)', borderRadius: '16px' }), background: 'var(--card-bg)', border: isMobile ? 'none' : '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 1001, overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {ccChatView === 'conversation' && (
                                <button onClick={() => { setCcChatView('list'); setCcChatWith(null); setCcChatWithName(null); loadCCConversations(); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px', display: 'flex' }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                                </button>
                            )}
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', position: 'relative', flexShrink: 0 }}>
                                {ccChatWith ? (
                                    <>
                                        <img src={`${API_URL}/api/profile/picture?username=${ccChatWith}`} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', position: 'absolute', inset: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #00AEEF, #0088cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700 }}>
                                            {(ccChatWithName || ccChatWith || 'U')[0].toUpperCase()}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #00AEEF, #0088cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700 }}>
                                        <Icons.MessageCircle />
                                    </div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '14px' }}>{ccChatWith ? (ccChatWithName && ccChatWithName !== ccChatWith ? ccChatWithName : `@${ccChatWith}`) : 'Messages'}</div>
                                {ccChatWith && ccChatWithName && ccChatWithName !== ccChatWith && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>@{ccChatWith}</div>}
                                {ccChatView === 'list' && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ccConversations.length} conversation{ccConversations.length !== 1 ? 's' : ''}</div>}
                            </div>
                        </div>
                        <button onClick={() => setShowCCChat(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px' }}>×</button>
                    </div>

                    {ccChatView === 'list' ? (
                        /* ── Conversation List ── */
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                            {ccConversations.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                    <Icons.MessageCircle />
                                    <p style={{ marginTop: '12px', fontSize: '13px' }}>No conversations yet</p>
                                    <p style={{ fontSize: '11px' }}>Start a conversation from the Chatters section</p>
                                </div>
                            ) : (
                                ccConversations.map(c => (
                                    <div key={c.other_username} onClick={() => { setCcChatMessages([]); setCcChatWith(c.other_username); setCcChatWithName(c.display_name || c.other_username); setCcChatView('conversation'); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '10px', cursor: 'pointer', marginBottom: '4px', background: 'transparent', transition: 'background 0.15s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        {c.profile_picture ? (
                                            <img src={`${API_URL}/api/profile/picture?username=${c.other_username}`} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                        ) : null}
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: c.profile_picture ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '15px', fontWeight: 700, flexShrink: 0 }}>
                                            {(c.display_name || c.other_username || 'U')[0].toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>{c.display_name && c.display_name !== c.other_username ? c.display_name : `@${c.other_username}`}{c.unread > 0 && <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#00AEEF', flexShrink: 0 }} />}</p>
                                                {c.last_message_at && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{new Date(c.last_message_at).toLocaleDateString()}</span>}
                                            </div>
                                            {c.display_name && c.display_name !== c.other_username && <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>@{c.other_username}</p>}
                                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {c.last_message || 'No messages yet'}
                                            </p>
                                        </div>
                                        {c.unread > 0 && (
                                            <span style={{ minWidth: '20px', height: '20px', borderRadius: '10px', background: '#00AEEF', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', flexShrink: 0 }}>{c.unread}</span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        /* ── Conversation View ── */
                        <>
                            {/* Messages */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column-reverse', gap: '8px' }}>
                                {ccChatLoading ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                        <div style={{ width: '28px', height: '28px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                    </div>
                                ) : ccChatMessages.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                                        <p style={{ fontSize: '13px' }}>Start a conversation with @{ccChatWith}</p>
                                    </div>
                                ) : (
                                    [...ccChatMessages].reverse().map(m => {
                                        const isMe = m.sender_username === user?.username;
                                        if (m.message_type === 'system') {
                                            return (
                                                <div key={m.id} style={{ textAlign: 'center', padding: '6px 12px' }}>
                                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '4px 12px', borderRadius: '12px' }}>{m.message}</span>
                                                </div>
                                            );
                                        }
                                        if (m.message_type === 'offer') {
                                            const offer = m.offer_data || {};
                                            return (
                                                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                                    <div style={{ maxWidth: '85%', padding: '14px', borderRadius: '12px', background: isMe ? 'rgba(0,174,239,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${isMe ? 'rgba(0,174,239,0.25)' : 'rgba(16,185,129,0.25)'}` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                                            <Icons.Briefcase />
                                                            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)' }}>Pricing Offer</span>
                                                            {m.offer_status === 'accepted' && <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(16,185,129,0.2)', color: '#10b981', fontWeight: 600 }}>Accepted</span>}
                                                            {m.offer_status === 'rejected' && <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(239,68,68,0.2)', color: '#ef4444', fontWeight: 600 }}>Declined</span>}
                                                            {m.offer_status === 'withdrawn' && <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(156,163,175,0.2)', color: '#9ca3af', fontWeight: 600 }}>Withdrawn</span>}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: m.message ? '8px' : '0' }}>
                                                            {(offer.pricing_type === 'hourly' || offer.pricing_type === 'both') && offer.hourly_rate && (
                                                                <div style={{ fontSize: '13px', color: 'var(--text)' }}><strong>${offer.hourly_rate}</strong>/hour</div>
                                                            )}
                                                            {(offer.pricing_type === 'percentage' || offer.pricing_type === 'both') && offer.percentage && (
                                                                <div style={{ fontSize: '13px', color: 'var(--text)' }}><strong>{offer.percentage}%</strong> of sales</div>
                                                            )}
                                                            {offer.channels && offer.channels.length > 0 && (
                                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Channels: {(offer.channel_names || offer.channels).map((name, i) => name || offer.channels[i]).join(', ')}</div>
                                                            )}
                                                        </div>
                                                        {m.message && <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 8px', fontStyle: 'italic' }}>{m.message}</p>}
                                                        {m.offer_status === 'pending' && isMe && (
                                                            <div style={{ marginTop: '8px' }}>
                                                                <button onClick={() => respondToOffer(m.id, 'withdraw')} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '6px 16px', fontSize: '11px', cursor: 'pointer', fontFamily: fontStack }}>Cancel Offer</button>
                                                            </div>
                                                        )}
                                                        {m.offer_status === 'pending' && !isMe && acceptingOffer !== m.id && (
                                                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                                                <button onClick={() => {
                                                                    const offerCh = offer.channels || [];
                                                                    if (!isChatter && wheels.length > 0) {
                                                                        setAcceptingOffer(m.id);
                                                                        setAcceptChannels(offerCh.length > 0 ? offerCh : wheels.map(w => w.channel_username));
                                                                    } else {
                                                                        respondToOffer(m.id, 'accept', offerCh);
                                                                    }
                                                                }} style={{ ...btnPrimary, width: 'auto', padding: '6px 16px', fontSize: '11px', background: '#10b981' }}>Accept</button>
                                                                <button onClick={() => respondToOffer(m.id, 'reject')} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '8px', padding: '6px 16px', fontSize: '11px', cursor: 'pointer', fontFamily: fontStack }}>Decline</button>
                                                            </div>
                                                        )}
                                                        {acceptingOffer === m.id && (
                                                            <div style={{ marginTop: '10px', padding: '10px', background: 'var(--surface-hover)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                                                <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Assign to channels:</p>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
                                                                    {wheels.map(w => (
                                                                        <div key={w.channel_username} onClick={() => setAcceptChannels(prev => prev.includes(w.channel_username) ? prev.filter(x => x !== w.channel_username) : [...prev, w.channel_username])}
                                                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: 'var(--text)', background: acceptChannels.includes(w.channel_username) ? 'rgba(0,174,239,0.06)' : 'transparent', border: `1px solid ${acceptChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}` }}>
                                                                            <div style={{ width: '14px', height: '14px', borderRadius: '3px', border: `2px solid ${acceptChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, background: acceptChannels.includes(w.channel_username) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                                {acceptChannels.includes(w.channel_username) && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                                                            </div>
                                                                            {w.title || w.channel_username}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                                    <button onClick={() => respondToOffer(m.id, 'accept', acceptChannels)} disabled={acceptChannels.length === 0}
                                                                        style={{ ...btnPrimary, width: 'auto', padding: '6px 14px', fontSize: '11px', background: '#10b981', opacity: acceptChannels.length === 0 ? 0.5 : 1 }}>Confirm</button>
                                                                    <button onClick={() => { setAcceptingOffer(null); setAcceptChannels([]); }}
                                                                        style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', cursor: 'pointer', fontFamily: fontStack }}>Cancel</button>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '6px' }}>
                                                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                            {isMe && (m.is_read ? (
                                                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L4 8.5" stroke="#00AEEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 8.5L10.5 2" stroke="#00AEEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 8.5L13.5 2" stroke="#00AEEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            ) : m.is_delivered ? (
                                                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L4 8.5" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 8.5L10.5 2" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 8.5L13.5 2" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            ) : (
                                                                <svg width="12" height="11" viewBox="0 0 12 11" fill="none"><path d="M1 5.5L4 8.5L10.5 2" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        const tickColor = m.is_read ? '#00AEEF' : 'var(--text-muted)';
                                        const tick = isMe ? (
                                            m.is_read ? (
                                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" style={{ flexShrink: 0 }}>
                                                    <path d="M1 5.5L4 8.5L4 8.5" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M4 8.5L10.5 2" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M7 8.5L13.5 2" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            ) : m.is_delivered ? (
                                                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" style={{ flexShrink: 0 }}>
                                                    <path d="M1 5.5L4 8.5L4 8.5" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M4 8.5L10.5 2" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M7 8.5L13.5 2" stroke={tickColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            ) : (
                                                <svg width="12" height="11" viewBox="0 0 12 11" fill="none" style={{ flexShrink: 0 }}>
                                                    <path d="M1 5.5L4 8.5L10.5 2" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )
                                        ) : null;
                                        return (
                                            <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                                <div style={{ maxWidth: '80%', padding: '10px 14px', borderRadius: isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isMe ? 'rgba(0,174,239,0.15)' : 'var(--surface-hover)', border: isMe ? '1px solid rgba(0,174,239,0.25)' : '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}>
                                                    <div>{m.message}</div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '4px' }}>
                                                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                        {tick}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            {/* Input */}
                            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                                <input
                                    type="text"
                                    value={ccChatInput}
                                    onChange={e => setCcChatInput(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && sendCCMessage()}
                                    placeholder="Type a message..."
                                    style={{ flex: 1, padding: '10px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '20px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, outline: 'none' }}
                                />
                                <button onClick={() => setShowOfferModal(true)} title="Send Offer"
                                    style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', border: 'none', color: '#10b981', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Icons.DollarSign />
                                </button>
                                <button onClick={sendCCMessage} disabled={!ccChatInput.trim() || ccSending}
                                    style={{ width: '38px', height: '38px', borderRadius: '50%', background: (ccChatInput.trim() && !ccSending) ? '#00AEEF' : 'var(--surface-hover)', border: 'none', color: (ccChatInput.trim() && !ccSending) ? '#fff' : 'var(--text-muted)', cursor: (ccChatInput.trim() && !ccSending) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Send Offer Modal */}
            {showOfferModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002, backdropFilter: 'blur(4px)' }} onClick={() => setShowOfferModal(false)}>
                    <div style={{ background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)', border: '1px solid var(--glass-border)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', padding: '28px 32px', width: '100%', maxWidth: '420px', margin: '20px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icons.Briefcase />
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Send Offer</h2>
                            </div>
                            <button onClick={() => setShowOfferModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 16px' }}>Send a pricing offer to @{ccChatWith}</p>

                        {/* Pricing Type */}
                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Pricing Type</label>
                            <CustomSelect value={offerPricingType} onChange={v => setOfferPricingType(v)}
                                options={[
                                    { value: 'hourly', label: 'Hourly Rate' },
                                    { value: 'percentage', label: '% of Sales' },
                                    { value: 'both', label: 'Both' },
                                ]} />
                        </div>

                        {/* Hourly Rate */}
                        {(offerPricingType === 'hourly' || offerPricingType === 'both') && (
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Hourly Rate (USD)</label>
                                <input type="number" min="0" max="100" step="0.5" value={offerHourlyRate} onChange={e => setOfferHourlyRate(e.target.value)} placeholder="e.g. 15" style={{ width: '100%', padding: '10px 12px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, boxSizing: 'border-box' }} />
                            </div>
                        )}

                        {/* Percentage */}
                        {(offerPricingType === 'percentage' || offerPricingType === 'both') && (
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Percentage of Sales</label>
                                <input type="number" min="1" max="100" step="1" value={offerPercentage} onChange={e => setOfferPercentage(e.target.value)} placeholder="e.g. 20" style={{ width: '100%', padding: '10px 12px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, boxSizing: 'border-box' }} />
                            </div>
                        )}

                        {/* Channels */}
                        {wheels.length > 0 && (
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Channels to assign</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '120px', overflowY: 'auto' }}>
                                    {wheels.map(w => (
                                        <div key={w.channel_username} onClick={() => setOfferChannels(prev => prev.includes(w.channel_username) ? prev.filter(x => x !== w.channel_username) : [...prev, w.channel_username])}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: offerChannels.includes(w.channel_username) ? 'rgba(0,174,239,0.06)' : 'var(--surface-hover)', border: `1px solid ${offerChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--text)' }}>
                                            <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: `2px solid ${offerChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, background: offerChannels.includes(w.channel_username) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {offerChannels.includes(w.channel_username) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                            </div>
                                            <ChannelAvatar username={w.channel_username} title={w.title} size={20} radius={5} />
                                            {w.title || w.channel_username}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Note */}
                        <div style={{ marginBottom: '18px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Note (optional)</label>
                            <textarea value={offerNote} onChange={e => setOfferNote(e.target.value)} placeholder="Add a note..." rows={2} style={{ width: '100%', padding: '10px 12px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setShowOfferModal(false)} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
                            <button onClick={sendOffer} disabled={!offerPricingType || ((offerPricingType === 'hourly' || offerPricingType === 'both') && !offerHourlyRate) || ((offerPricingType === 'percentage' || offerPricingType === 'both') && !offerPercentage)}
                                style={{ ...btnPrimary, flex: 1, opacity: (!offerPricingType || ((offerPricingType === 'hourly' || offerPricingType === 'both') && !offerHourlyRate) || ((offerPricingType === 'percentage' || offerPricingType === 'both') && !offerPercentage)) ? 0.5 : 1 }}>Send Offer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {/* Delete Channel Confirmation Modal */}
            {deleteChannelConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, backdropFilter: 'blur(4px)' }} onClick={() => { setDeleteChannelConfirm(null); setDeleteConfirmText(''); }}>
                    <div style={{ background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)', border: '1px solid var(--glass-border)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', padding: '30px 35px', width: '100%', maxWidth: '400px', margin: '20px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--error)' }}>
                            <Icons.Trash />
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Delete Channel</h3>
                        <p style={{ fontSize: '14px', color: 'var(--text)', margin: '0 0 4px' }}>@{deleteChannelConfirm}</p>
                        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '14px', margin: '16px 0', textAlign: 'left' }}>
                            <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--error)', margin: '0 0 8px' }}>This will permanently delete:</p>
                            <ul style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, paddingLeft: '18px', lineHeight: '1.8' }}>
                                <li>Spin configuration and prizes</li>
                                <li>All subscriber data and VIP settings</li>
                                <li>Transaction history and earnings</li>
                                <li>Vault media and paid content</li>
                                <li>DM history and chatter assignments</li>
                            </ul>
                        </div>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 6px', textAlign: 'left' }}>Type <strong style={{ color: 'var(--text)' }}>I understand</strong> to confirm:</p>
                        <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={e => setDeleteConfirmText(e.target.value)}
                            placeholder="I understand"
                            style={{ width: '100%', padding: '10px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack, boxSizing: 'border-box', marginBottom: '16px', outline: 'none' }}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => { setDeleteChannelConfirm(null); setDeleteConfirmText(''); }} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
                            <button onClick={() => { deleteWheel(deleteChannelConfirm); setDeleteConfirmText(''); }} disabled={deleteConfirmText.trim().toLowerCase() !== 'i understand'} style={{ flex: 1, padding: '10px 20px', background: deleteConfirmText.trim().toLowerCase() === 'i understand' ? 'var(--error)' : 'var(--surface-hover)', color: deleteConfirmText.trim().toLowerCase() === 'i understand' ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: '30px', cursor: deleteConfirmText.trim().toLowerCase() === 'i understand' ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '13px', fontFamily: fontStack, transition: 'all 0.2s' }}>Delete Channel</button>
                        </div>
                    </div>
                </div>
            )}


            {/* Reset to Default Confirmation Modal */}
            {showResetConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, backdropFilter: 'blur(4px)' }} onClick={() => setShowResetConfirm(false)}>
                    <div style={{ background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)', border: '1px solid var(--glass-border)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', padding: '30px 35px', width: '100%', maxWidth: '380px', margin: '20px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>Reset to Default</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 20px', lineHeight: 1.5 }}>This will reset all colors, texts, prizes, and packages to their defaults. Your banner will be kept.</p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setShowResetConfirm(false)} style={{ ...btnSecondary, flex: 1 }}>Cancel</button>
                            <button onClick={() => { setConfig({ ...defConfig, banner_file_id: config.banner_file_id, banner_type: config.banner_type }); setShowResetConfirm(false); toast.success('Reset to defaults. Click Save Changes to apply.'); }} style={{ flex: 1, padding: '10px 20px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', fontFamily: fontStack, transition: 'all 0.2s' }}>Reset</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payments Modal (only used for admin viewing chatter earnings now — main payments is inline) */}

            {/* Chatter Earnings Modal (admin viewing a chatter) */}
            {viewChatterEarnings && <PaymentsModal onClose={() => { setViewChatterEarnings(null); }} wheels={wheels} isChatter={true} adminViewChatter={viewChatterEarnings} />}


            {/* Add Channel Modal (Chatter) */}
            {showAddChannelCode && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={() => { setShowAddChannelCode(false); setInviteCodeInput(''); setCreatorUsernameInput(''); }}>
                    <div style={{ background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)', border: '1px solid var(--glass-border)', borderRadius: '16px', boxShadow: 'var(--card-shadow)', padding: '28px 32px', width: '100%', maxWidth: '420px', margin: '20px' }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icons.Plus />
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add Channel</h2>
                            </div>
                            <button onClick={() => { setShowAddChannelCode(false); setInviteCodeInput(''); setCreatorUsernameInput(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: '4px', background: 'var(--surface)', borderRadius: '10px', padding: '4px', marginBottom: '22px' }}>
                            {[{ key: 'code', label: 'Invite Code' }, { key: 'request', label: 'Send Request' }].map(tab => (
                                <button key={tab.key} onClick={() => setAddChannelTab(tab.key)}
                                    style={{ flex: 1, padding: '8px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px', fontFamily: fontStack, transition: 'all 0.15s',
                                        background: addChannelTab === tab.key ? 'var(--card-bg)' : 'transparent',
                                        color: addChannelTab === tab.key ? 'var(--text)' : 'var(--text-muted)',
                                        boxShadow: addChannelTab === tab.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none' }}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {addChannelTab === 'code' ? (
                            <>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px' }}>Enter the invite code from your creator to get instant access.</p>
                                <div style={{ marginBottom: '16px' }}>
                                    <input
                                        type="text"
                                        value={inviteCodeInput}
                                        onChange={e => setInviteCodeInput(e.target.value.toUpperCase())}
                                        placeholder="Enter invite code"
                                        style={{ ...dInput, textAlign: 'center', fontSize: '18px', fontWeight: 700, letterSpacing: '2px', fontFamily: 'monospace' }}
                                        onKeyDown={e => e.key === 'Enter' && redeemInviteCode()}
                                        autoFocus
                                    />
                                </div>
                                <button onClick={redeemInviteCode} disabled={redeemingInvite || !inviteCodeInput.trim()} style={{ ...btnPrimary, width: '100%', padding: '14px', fontSize: '14px', opacity: redeemingInvite ? 0.7 : 1 }}>
                                    {redeemingInvite ? 'Redeeming...' : 'Redeem Code'}
                                </button>
                            </>
                        ) : (
                            <>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px' }}>Enter a creator's username to send them a connection request. They'll be notified and can approve you.</p>
                                <div style={{ marginBottom: '16px' }}>
                                    <input
                                        type="text"
                                        value={creatorUsernameInput}
                                        onChange={e => setCreatorUsernameInput(e.target.value)}
                                        placeholder="@creatorusername"
                                        style={{ ...dInput }}
                                        onKeyDown={e => e.key === 'Enter' && sendConnectRequest()}
                                        autoFocus
                                    />
                                </div>
                                <button onClick={sendConnectRequest} disabled={sendingRequest || !creatorUsernameInput.trim()} style={{ ...btnPrimary, width: '100%', padding: '14px', fontSize: '14px', opacity: sendingRequest ? 0.7 : 1 }}>
                                    {sendingRequest ? 'Sending...' : 'Send Request'}
                                </button>
                                {chatterRequests.length > 0 && (
                                    <div style={{ marginTop: '20px' }}>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your Requests</p>
                                        {chatterRequests.map(r => (
                                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface)', borderRadius: '8px', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '13px', color: 'var(--text)' }}>@{r.creator_username}</span>
                                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                                    background: r.status === 'approved' ? 'rgba(34,197,94,0.15)' : r.status === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(var(--accent-rgb),0.12)',
                                                    color: r.status === 'approved' ? '#22c55e' : r.status === 'rejected' ? '#ef4444' : 'var(--accent)' }}>
                                                    {r.status === 'approved' ? 'Approved' : r.status === 'rejected' ? 'Rejected' : 'Pending'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

                {/* Content Area */}
                {showSettings ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '32px 40px' }}>
                        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
                                <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Settings</h1>
                                <button onClick={() => { navigate('/dashboard'); setNewPassword(''); setConfirmPassword(''); setCurrentPassword(''); }}
                                    style={{ ...btnSecondary, padding: '8px 16px', fontSize: '12px' }}>Done</button>
                            </div>

                            {/* Profile Picture */}
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Profile Picture</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    {adminProfilePic ? (
                                        <img src={`${API_URL}/api/profile/picture?username=${user?.username}&t=${Date.now()}`} alt=""
                                            style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} />
                                    ) : (
                                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '28px', fontWeight: 700, flexShrink: 0 }}>
                                            {(user?.username || '?')[0].toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <label style={{ ...btnSecondary, padding: '8px 20px', fontSize: '13px', display: 'inline-block' }}>
                                            Change Photo
                                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
                                                const formData = new FormData();
                                                formData.append('image', file);
                                                formData.append('token', localStorage.getItem('token'));
                                                try {
                                                    const res = await api.post('/api/profile/upload-picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                                    setAdminProfilePic(res.data.profile_picture);
                                                    toast.success('Profile picture updated');
                                                } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
                                            }} />
                                        </label>
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '8px 0 0' }}>JPG, PNG or WebP. Max 5MB.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Nickname (admin) */}
                            {!isChatter && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Nickname</h3>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input type="text" value={editingDisplayName} onChange={e => setEditingDisplayName(e.target.value)}
                                        placeholder={user?.username || 'Enter nickname'} maxLength={50}
                                        style={{ ...dInput, flex: 1 }} />
                                    <button onClick={async () => {
                                        setSavingProfile(true);
                                        try {
                                            await api.post('/api/dashboard/profile/update', { display_name: editingDisplayName });
                                            setAdminDisplayName(editingDisplayName);
                                            toast.success('Nickname updated');
                                        } catch (err) { toast.error(err.response?.data?.error || 'Failed to update'); }
                                        finally { setSavingProfile(false); }
                                    }} disabled={savingProfile || editingDisplayName === adminDisplayName}
                                        style={{ ...btnPrimary, width: 'auto', padding: '10px 20px', fontSize: '13px', opacity: (savingProfile || editingDisplayName === adminDisplayName) ? 0.5 : 1 }}>
                                        {savingProfile ? '...' : 'Save'}
                                    </button>
                                </div>
                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '8px 0 0' }}>Username: @{user?.username}</p>
                            </div>
                            )}

                            {/* Email */}
                            {!isChatter && adminEmail && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Email</h3>
                                <div style={{ ...dInput, background: 'var(--surface-hover)', color: 'var(--text-muted)', cursor: 'default' }}>{adminEmail}</div>
                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '8px 0 0' }}>Email cannot be changed. Contact support if you need to update it.</p>
                            </div>
                            )}


                            {/* Chatter Profile */}
                            {isChatter && editChatter && (
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Profile</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Username</label>
                                        <div style={{ ...dInput, background: 'var(--surface-hover)', color: 'var(--text-muted)', cursor: 'default' }}>@{user?.username}</div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Nickname</label>
                                        <input type="text" value={editChatter.display_name || ''} onChange={e => setEditChatter(p => ({ ...p, display_name: e.target.value }))}
                                            placeholder="Your nickname" maxLength={50} style={dInput} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Bio</label>
                                        <textarea value={editChatter.bio || ''} onChange={e => setEditChatter(p => ({ ...p, bio: e.target.value }))}
                                            placeholder="Tell creators about yourself..." maxLength={500} rows={3}
                                            style={{ ...dInput, resize: 'vertical', minHeight: '70px' }} />
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '4px 0 0', textAlign: 'right' }}>{(editChatter.bio || '').length}/500</p>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Timezone</label>
                                        <CustomSelect value={editChatter.timezone || ''} onChange={v => setEditChatter(p => ({ ...p, timezone: v }))}
                                            options={(() => { try { return Intl.supportedValuesOf('timeZone').map(tz => { try { const f = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }); const p = f.formatToParts(new Date()); const off = p.find(x => x.type === 'timeZoneName')?.value || ''; return { value: tz, label: `${tz.replace(/_/g, ' ')} (${off})` }; } catch { return { value: tz, label: tz.replace(/_/g, ' ') }; } }); } catch { return []; } })()}
                                            placeholder="Select timezone" searchable />
                                    </div>
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>Languages</label>
                                            <button type="button" onClick={() => setEditChatter(p => ({ ...p, languages: [...(p.languages || []), { lang: '', level: 'Fluent' }] }))}
                                                style={{ fontSize: '12px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                                                + Add
                                            </button>
                                        </div>
                                        {(editChatter.languages || []).map((lang, i) => {
                                            const langObj = typeof lang === 'string' ? { lang, level: 'Fluent' } : lang;
                                            const usedLangs = (editChatter.languages || []).map(l => typeof l === 'string' ? l : l.lang);
                                            const LANG_LIST = ['English','Spanish','French','German','Italian','Portuguese','Russian','Chinese','Japanese','Korean','Arabic','Hindi','Turkish','Dutch','Polish','Swedish','Norwegian','Danish','Finnish','Czech','Romanian','Hungarian','Greek','Thai','Vietnamese','Indonesian','Malay','Filipino','Ukrainian','Hebrew','Persian','Bengali','Urdu','Swahili'];
                                            return (
                                                <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center' }}>
                                                    <CustomSelect value={langObj.lang} onChange={v => setEditChatter(p => {
                                                        const langs = [...(p.languages || [])];
                                                        langs[i] = { ...(typeof langs[i] === 'string' ? { lang: langs[i], level: 'Fluent' } : langs[i]), lang: v };
                                                        return { ...p, languages: langs };
                                                    })}
                                                        options={LANG_LIST.filter(l => l === langObj.lang || !usedLangs.includes(l)).map(l => ({ value: l, label: l }))}
                                                        placeholder="Select language" searchable style={{ flex: 1 }} />
                                                    <CustomSelect value={langObj.level} onChange={v => setEditChatter(p => {
                                                        const langs = [...(p.languages || [])];
                                                        langs[i] = { ...(typeof langs[i] === 'string' ? { lang: langs[i], level: 'Fluent' } : langs[i]), level: v };
                                                        return { ...p, languages: langs };
                                                    })}
                                                        options={[{ value: 'Native', label: 'Native' }, { value: 'Fluent', label: 'Fluent' }, { value: 'Basic', label: 'Basic' }]}
                                                        style={{ width: '100px', flex: 'none' }} />
                                                    {(editChatter.languages || []).length > 1 && (
                                                        <button type="button" onClick={() => setEditChatter(p => ({ ...p, languages: (p.languages || []).filter((_, idx) => idx !== i) }))}
                                                            style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '16px', padding: '4px', lineHeight: 1, flexShrink: 0 }}>
                                                            &times;
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {(editChatter.languages || []).length === 0 && (
                                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No languages added</p>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Pricing Type</label>
                                        <CustomSelect value={editChatter.pricing_type || 'negotiable'} onChange={v => setEditChatter(p => ({ ...p, pricing_type: v }))}
                                            options={[
                                                { value: 'negotiable', label: 'Negotiable' },
                                                { value: 'hourly', label: 'Hourly Rate' },
                                                { value: 'percentage', label: '% of Sales' },
                                                { value: 'both', label: 'Both (Hourly + %)' },
                                            ]} />
                                    </div>
                                    {(editChatter.pricing_type === 'hourly' || editChatter.pricing_type === 'percentage' || editChatter.pricing_type === 'both') && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        {(editChatter.pricing_type === 'hourly' || editChatter.pricing_type === 'both') && (
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Hourly Rate (USD)</label>
                                            <input type="number" min="0" max="100" step="0.5" value={editChatter.proposed_hourly_rate || ''}
                                                onChange={e => setEditChatter(p => ({ ...p, proposed_hourly_rate: e.target.value }))}
                                                placeholder="e.g. 15" style={dInput} />
                                        </div>
                                        )}
                                        {(editChatter.pricing_type === 'percentage' || editChatter.pricing_type === 'both') && (
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Percentage of Sales (%)</label>
                                            <input type="number" min="0" max="100" step="1" value={editChatter.proposed_percentage || ''}
                                                onChange={e => setEditChatter(p => ({ ...p, proposed_percentage: e.target.value }))}
                                                placeholder="e.g. 20" style={dInput} />
                                        </div>
                                        )}
                                    </div>
                                    )}
                                    <button onClick={async () => {
                                        if (!editChatter.display_name?.trim()) { toast.error('Nickname is required'); return; }
                                        setSavingProfile(true);
                                        try {
                                            await api.post('/api/chatter/save-profile', {
                                                display_name: editChatter.display_name,
                                                bio: editChatter.bio,
                                                timezone: editChatter.timezone,
                                                languages: editChatter.languages,
                                                pricing_type: editChatter.pricing_type,
                                                proposed_hourly_rate: editChatter.proposed_hourly_rate || null,
                                                proposed_percentage: editChatter.proposed_percentage || null,
                                            });
                                            setChatterProfile(editChatter);
                                            toast.success('Profile updated');
                                        } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
                                        finally { setSavingProfile(false); }
                                    }} disabled={savingProfile} style={{ ...btnPrimary, width: '100%', opacity: savingProfile ? 0.5 : 1 }}>
                                        {savingProfile ? 'Saving...' : 'Save Profile'}
                                    </button>
                                </div>
                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '10px 0 0' }}>Username: @{user?.username}</p>
                            </div>
                            )}

                            {/* Telegram Connection (admin only) */}
                            {!isChatter && <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Telegram</h3>
                                {telegramConnected ? (
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: 'var(--surface-hover)', borderRadius: '10px', marginBottom: '14px' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                                                <Icons.Send />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{tgUsername ? (tgUsername.match(/^[a-zA-Z0-9_]+$/) ? `@${tgUsername}` : tgUsername) : 'Connected'}</p>
                                                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>ID: {tgId}</p>
                                            </div>
                                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                                        </div>
                                        <button onClick={async () => {
                                            if (!await confirm({ title: 'Disconnect Telegram?', message: 'You will need to reconnect to add new channels.', confirmLabel: 'Disconnect', danger: true })) return;
                                            try {
                                                const res = await api.post('/api/dashboard/disconnect-telegram', { token: localStorage.getItem('token') });
                                                setTelegramConnected(false);
                                                setTgUsername('');
                                                setTgId(null);
                                                setConnectCode(res.data.connect_code || '');
                                                toast.success('Telegram disconnected');
                                            } catch (err) { toast.error(err.response?.data?.error || 'Failed to disconnect'); }
                                        }} style={{ ...btnSecondary, width: '100%', fontSize: '13px', color: 'var(--error)', borderColor: 'rgba(239,68,68,0.3)' }}>
                                            Disconnect Telegram
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px' }}>Connect your Telegram account to manage channels and receive notifications.</p>
                                        <button onClick={() => setShowTgConnect(true)}
                                            style={{ ...btnPrimary, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxSizing: 'border-box' }}>
                                            <Icons.Send /> Connect Telegram
                                        </button>
                                    </div>
                                )}
                            </div>}

                            {/* Password */}
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Change Password</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Current Password</label>
                                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" style={dInput} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>New Password</label>
                                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" style={dInput} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-muted)' }}>Confirm Password</label>
                                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" style={dInput} />
                                    </div>
                                    <button onClick={changePassword} style={{ ...btnPrimary, width: '100%' }}>Change Password</button>
                                </div>
                            </div>

                        </div>
                    </div>
                ) : showPayments ? (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <PaymentsModal onClose={() => navigate(-1)} wheels={wheels} isChatter={isChatter} fullPage={true} />
                    </div>
                ) : showReferrals ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '24px 28px' }}>
                        <ReferralsTab isMobile={isMobile} hasChannels={wheels.length > 0} />
                    </div>
                ) : channelTab === 'overview' ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '24px 28px' }}>
                        <OverviewTab wheels={wheels} username={user?.username} onNavigate={(path) => navigate(path)} isMobile={isMobile} />
                    </div>
                ) : channelTab === 'guide' ? (
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <GuideTab isChatter={isChatter} isMobile={isMobile} />
                    </div>
                ) : channelTab === 'chatters' ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '24px 28px' }}>
                        <ChattersTab wheels={wheels} isMobile={isMobile} onViewEarnings={(c) => setViewChatterEarnings(c)} onStartChat={startCCChat} />
                    </div>
                ) : (channelTab === 'dms' && selectedWheel) || (isChatter && channelTab === 'wheel' && selectedWheel) ? (
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {(() => {
                            const w = wheels.find(wh => wh.channel_username === selectedWheel);
                            if (w && !w.linked_chat_id && !isChatter) return (
                                <div style={{ margin: '12px 16px 0', padding: '14px 18px', borderRadius: '10px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)', fontSize: '13px', lineHeight: 1.6, flexShrink: 0 }}>
                                    <strong style={{ color: '#ef4444' }}>Direct Messages not enabled</strong>
                                    <span style={{ color: '#991b1b' }}> — Enable direct messages on your channel to receive subscriber DMs.</span>
                                    <div style={{ marginTop: '8px', color: '#6b7280', fontSize: '12px' }}>
                                        <strong>How to enable:</strong>
                                        <ol style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                                            <li>Open your channel in Telegram</li>
                                            <li>Tap the channel name at the top</li>
                                            <li>Go to <strong>Settings</strong></li>
                                            <li>Enable <strong>Direct Messages</strong></li>
                                        </ol>
                                    </div>
                                </div>
                            );
                            return null;
                        })()}
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <ChannelDMsTab key={selectedWheel} channel={selectedWheel} isMobile={isMobile} />
                        </div>
                    </div>
                ) : channelTab === 'vault' && selectedWheel ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px' }}>
                        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                            <VaultTab channel={selectedWheel} isChatter={isChatter} />
                        </div>
                    </div>
                ) : channelTab === 'subs' && selectedWheel ? (
                    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px' }}>
                        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                            <SubscriptionsTab channel={selectedWheel} />
                        </div>
                    </div>
                ) : (
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '20px' }}>
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>

                    {/* Show wheel tab or welcome */}
                    {!selectedWheel ? (
                        <div style={{
                            background: 'var(--card-bg)',
                            backdropFilter: 'var(--glass-blur)',
                            WebkitBackdropFilter: 'var(--glass-blur)',
                            border: '1px solid var(--card-border)',
                            borderRadius: '14px',
                            padding: '60px',
                            textAlign: 'center',
                            boxShadow: 'var(--card-shadow)'
                        }}>
                            {isChatter ? (
                                <>
                                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>💬</div>
                                    <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '10px', color: 'var(--text)' }}>No Channels Yet</h2>
                                    <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>Enter an invite code from your admin to get access to channels.</p>
                                    <button onClick={() => { setAddChannelTab('code'); loadChatterRequests(); setShowAddChannelCode(true); }} style={{ padding: '12px 24px', background: 'var(--accent)', color: 'var(--btn-primary-text)', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', fontFamily: fontStack, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: 'var(--btn-primary-shadow)' }}><Icons.Plus /> Add Channel</button>
                                </>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '20px' }}><img src={'/logo-light.webp'} alt="Privly" style={{ height: '48px' }} /></div>
                                    <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '10px', color: 'var(--text)' }}>Welcome!</h2>
                                    <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>Click the button below to add your first channel</p>
                                    <button onClick={requestAddChannel} disabled={addingChannel} style={{ padding: '12px 24px', background: 'var(--accent)', color: 'var(--btn-primary-text)', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', fontFamily: fontStack, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: 'var(--btn-primary-shadow)' }}>{addingChannel ? <><Icons.Clock /> Waiting...</> : <><Icons.Plus /> Add Channel</>}</button>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            {!spinWheelEnabled && (
                                <div style={{ background: 'linear-gradient(135deg, #f0c040 0%, #e6a817 100%)', color: '#000', padding: '12px 18px', borderRadius: '10px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 600 }}>
                                    <span style={{ fontSize: '18px' }}>🎡</span>
                                    <span>Spin Wheel is coming soon! You can configure your wheel now — posting and purchases will be enabled when we launch.</span>
                                </div>
                            )}
                            {/* Link Card - hidden when spin wheel is disabled */}
                            {spinWheelEnabled && (
                            <div style={{
                                background: 'var(--card-bg)',
                                backdropFilter: 'var(--glass-blur)',
                                WebkitBackdropFilter: 'var(--glass-blur)',
                                border: '1px solid var(--card-border)',
                                borderRadius: '14px',
                                padding: '18px',
                                marginBottom: '16px',
                                boxShadow: 'var(--card-shadow)'
                            }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: 'var(--text)', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><Icons.Link /> Your Channel Link</h3>
                                <div style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', padding: '12px 14px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '12px', marginBottom: '12px', wordBreak: 'break-all', color: 'var(--text)' }}>{deepLink}</div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button onClick={() => { navigator.clipboard.writeText(deepLink); toast.success('Copied to clipboard'); }} style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}><Icons.Copy /> Copy</button>
                                    <button onClick={postChannel} style={{ ...btnSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}><Icons.Send /> Post to Channel</button>
                                    <a href={deepLink} target="_blank" rel="noopener noreferrer" style={{ ...btnSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}><Icons.ExternalLink /> Test</a>
                                </div>
                            </div>
                            )}

                            {/* Tabs - Glass */}
                            <div style={{ 
                                background: 'var(--card-bg)', 
                                backdropFilter: 'var(--glass-blur)',
                                WebkitBackdropFilter: 'var(--glass-blur)',
                                border: '1px solid var(--card-border)', 
                                borderRadius: '14px', 
                                overflow: 'hidden', 
                                boxShadow: 'var(--card-shadow)' 
                            }}>
                                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                                    {[{ id: 'general', label: 'General', icon: Icons.Settings }, { id: 'prizes', label: 'Prizes', icon: Icons.Gift }, { id: 'colors', label: 'Colors', icon: Icons.Palette }, { id: 'packages', label: 'Packages', icon: Icons.DollarSign }].map(tab => (
                                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '14px', background: 'transparent', border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', color: activeTab === tab.id ? 'var(--text)' : 'var(--text-muted)', fontWeight: 600, cursor: 'pointer', fontSize: '13px', fontFamily: fontStack, transition: 'all 0.15s ease', minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><tab.icon /> {tab.label}</button>
                                    ))}
                                </div>
                                <div style={{ padding: '22px', display: 'flex', gap: '24px' }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    {activeTab === 'general' && (
                                        <div>
                                            {/* Telegram Post Preview */}
                                            <div style={{ marginBottom: '24px' }}>
                                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preview</label>
                                                <div style={{ background: '#17212b', borderRadius: '12px', overflow: 'hidden', maxWidth: '360px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
                                                    {/* Channel name */}
                                                    <div style={{ padding: '8px 12px 4px', fontSize: '13px', fontWeight: 700, color: '#6ab2f2' }}>
                                                        {selectedWheelObj?.title || `@${selectedWheelObj?.channel_username || 'channel'}`}
                                                    </div>
                                                    {/* Banner image */}
                                                    {config.banner_file_id ? (
                                                        <img src={`${API_URL}/api/dashboard/file?auth=${encodeURIComponent(localStorage.getItem('token')||'')}&file_id=${encodeURIComponent(config.banner_file_id)}`} alt="" style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', display: 'block', background: '#0e1621' }} />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '120px', background: 'linear-gradient(135deg, #1e2a3a 0%, #2a3a4a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4a5568" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                                        </div>
                                                    )}
                                                    {/* Description */}
                                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#e1e4e8', lineHeight: 1.4 }}>
                                                        {config.banner_description || 'Spin to win exclusive content!'}
                                                    </div>
                                                    {/* Button */}
                                                    <div style={{ padding: '4px 8px 8px' }}>
                                                        <div style={{ background: 'rgba(106,178,242,0.15)', borderRadius: '6px', padding: '8px', textAlign: 'center', fontSize: '13px', fontWeight: 500, color: '#6ab2f2', cursor: 'default' }}>
                                                            {config.spin_button_text || 'Play now!'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--text-secondary)' }}>Banner Description</label>
                                                <textarea value={config.banner_description} onChange={e => setConfig({ ...config, banner_description: e.target.value })} style={{ ...dInput, fontSize: '14px', minHeight: '70px', resize: 'vertical' }} placeholder="Spin to win exclusive content!" />
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Description shown in channel posts</div>
                                            </div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--text-secondary)' }}>Button Text</label>
                                                <input value={config.spin_button_text} onChange={e => setConfig({ ...config, spin_button_text: e.target.value })} style={{ ...dInput, fontSize: '14px' }} placeholder="Play now!" />
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Text on the button in channel posts</div>
                                            </div>
                                            <div style={{ marginBottom: '20px' }}>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--text-secondary)' }}>App Title</label>
                                                <input value={config.app_title} onChange={e => setConfig({ ...config, app_title: e.target.value })} style={{ ...dInput, fontSize: '14px' }} placeholder="App title (optional)" />
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Title shown at the top of the mini app</div>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '8px', color: 'var(--text-secondary)' }}>Banner (JPG/PNG only, max 10MB)</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                    {config.banner_file_id && thumbnails[config.banner_file_id] ? (
                                                        <div style={{ position: 'relative' }}>
                                                            <img 
                                                                src={thumbnails[config.banner_file_id]} 
                                                                alt="Banner" 
                                                                style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }} 
                                                            />
                                                            <button onClick={rmBanner} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', background: 'var(--error)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>×</button>
                                                        </div>
                                                    ) : config.banner_file_id ? (
                                                        <div style={{ position: 'relative', width: '120px', height: '80px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                                                            <Icons.Image />
                                                            <button onClick={rmBanner} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>×</button>
                                                        </div>
                                                    ) : null}
                                                    <label style={{ ...btnSecondary, cursor: 'pointer' }}>{config.banner_file_id ? 'Change' : 'Upload'}<input type="file" accept=".jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadFile(e.target.files[0], 'banner')} /></label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'prizes' && (
                                        <div>
                                            {/* Prize Count Selector */}
                                            <div style={{ marginBottom: '20px', padding: '14px', background: 'var(--surface-hover)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '10px', color: 'var(--text-secondary)' }}>Number of Prizes</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <input 
                                                        type="range" 
                                                        min="2" 
                                                        max="12" 
                                                        value={config.prize_count || 12} 
                                                        onChange={e => {
                                                            const count = parseInt(e.target.value);
                                                            // Ensure we have enough prizes
                                                            let prizes = [...config.prizes];
                                                            while (prizes.length < count) {
                                                                prizes.push({ index: prizes.length, label: `Prize ${prizes.length + 1}`, file_ids: [], types: [] });
                                                            }
                                                            // Reset all custom chances when count changes to avoid broken percentages
                                                            prizes = prizes.map(p => {
                                                                const { chance, ...rest } = p;
                                                                return rest;
                                                            });
                                                            setConfig({ ...config, prize_count: count, prizes });
                                                        }}
                                                        style={{ flex: 1 }}
                                                    />
                                                    <span style={{ minWidth: '36px', textAlign: 'center', fontWeight: 600, color: 'var(--text)', fontSize: '16px' }}>{config.prize_count || 12}</span>
                                                </div>
                                            </div>
                                            
                                            {/* Hidden Chances UI - only show if enabled */}
                                            {chancesEnabled && (() => {
                                                const prizeCount = config.prize_count || 12;
                                                const allApplied = config.prizes.slice(0, prizeCount).every(p => p.chance !== undefined);
                                                const customCount = config.prizes.slice(0, prizeCount).filter(p => p.chance !== undefined).length;
                                                const total = config.prizes.slice(0, prizeCount).reduce((sum, p) => sum + (p.chance || 0), 0);
                                                
                                                return (
                                                <div style={{ marginBottom: '20px', padding: '14px', background: 'rgba(99,102,241,0.1)', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.3)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                        <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--accent)' }}>Prize Probability</label>
                                                        {customCount > 0 && (
                                                            <button 
                                                                onClick={() => {
                                                                    const np = config.prizes.map(p => {
                                                                        const { chance, ...rest } = p;
                                                                        return rest;
                                                                    });
                                                                    setConfig({ ...config, prizes: np });
                                                                }}
                                                                style={{ ...btnSmall, padding: '4px 10px', fontSize: '11px' }}
                                                            >Reset All</button>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Warning */}
                                                    <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '12px', padding: '8px 10px', background: 'rgba(245,158,11,0.1)', borderRadius: '6px', border: '1px solid rgba(245,158,11,0.2)' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px', borderRadius: '50%', background: 'rgba(245,158,11,0.3)', marginRight: '6px', fontSize: '10px', fontWeight: 700 }}>!</span>Keep probabilities realistic to avoid suspicious patterns. Extreme values may affect user trust.
                                                    </div>
                                                    
                                                    {allApplied ? (
                                                        // Locked state - show read-only values
                                                        <>
                                                            <p style={{ fontSize: '11px', color: '#10b981', marginBottom: '12px', marginTop: 0 }}>
                                                                <Icons.Check /> Probabilities applied. Click "Reset All" to edit.
                                                            </p>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '6px', marginBottom: '10px' }}>
                                                                {config.prizes.slice(0, prizeCount).map((p, i) => (
                                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 8px', background: 'var(--surface-hover)', borderRadius: '6px' }}>
                                                                        <span style={{ fontSize: '11px', color: 'var(--accent)', fontWeight: 600 }}>{i+1}.</span>
                                                                        <span style={{ fontSize: '12px', color: 'var(--text)' }}>{p.chance?.toFixed(1)}%</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#10b981' }}>
                                                                Total: {total.toFixed(1)}% <Icons.Check />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        // Edit state - allow setting up to 2 custom values
                                                        <>
                                                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px', marginTop: 0 }}>
                                                                Enter custom % for up to 2 prizes, then click "Apply" to auto-fill the rest.
                                                            </p>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                                                                {config.prizes.slice(0, prizeCount).map((p, i) => {
                                                                    const defaultChance = (100 / prizeCount).toFixed(1);
                                                                    const isCustom = p.chance !== undefined;
                                                                    const canEdit = isCustom || customCount < 2;
                                                                    
                                                                    return (
                                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <span style={{ fontSize: '11px', color: isCustom ? '#a5b4fc' : 'var(--text-muted)', minWidth: '18px', fontWeight: isCustom ? 600 : 400 }}>{i+1}</span>
                                                                            <input 
                                                                                type="number"
                                                                                step="0.1"
                                                                                min="0.1"
                                                                                max="99"
                                                                                value={isCustom ? p.chance : ''}
                                                                                placeholder={defaultChance}
                                                                                disabled={!canEdit}
                                                                                onChange={e => {
                                                                                    const np = [...config.prizes];
                                                                                    const val = e.target.value;
                                                                                    if (val === '' || val === null) {
                                                                                        // Remove chance property
                                                                                        const { chance, ...rest } = np[i];
                                                                                        np[i] = rest;
                                                                                    } else {
                                                                                        const num = parseFloat(val);
                                                                                        if (!isNaN(num) && num >= 0) {
                                                                                            np[i] = { ...np[i], chance: num };
                                                                                        }
                                                                                    }
                                                                                    setConfig({ ...config, prizes: np });
                                                                                }}
                                                                                style={{ 
                                                                                    ...dInput, 
                                                                                    width: '58px', 
                                                                                    padding: '5px 6px', 
                                                                                    fontSize: '12px',
                                                                                    textAlign: 'center',
                                                                                    background: isCustom ? 'rgba(99,102,241,0.25)' : 'var(--input-bg)',
                                                                                    borderColor: isCustom ? 'rgba(99,102,241,0.5)' : 'var(--border)',
                                                                                    opacity: canEdit ? 1 : 0.4
                                                                                }} 
                                                                            />
                                                                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>%</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            
                                                            {/* Apply button */}
                                                            <button 
                                                                onClick={() => {
                                                                    const np = [...config.prizes];
                                                                    
                                                                    // Find custom set prizes
                                                                    const customIndices = [];
                                                                    let customTotal = 0;
                                                                    
                                                                    for (let i = 0; i < prizeCount; i++) {
                                                                        if (np[i]?.chance !== undefined) {
                                                                            customIndices.push(i);
                                                                            customTotal += np[i].chance;
                                                                        }
                                                                    }
                                                                    
                                                                    // Validate
                                                                    const autoPrizeCount = prizeCount - customIndices.length;
                                                                    if (customTotal > 100) {
                                                                        toast.error(`Total ${customTotal.toFixed(1)}% exceeds 100%`);
                                                                        return;
                                                                    }
                                                                    if (autoPrizeCount > 0 && customTotal >= 100) {
                                                                        toast.error(`No room left for other ${autoPrizeCount} prizes`);
                                                                        return;
                                                                    }
                                                                    
                                                                    // Calculate remaining for auto-fill prizes
                                                                    const remaining = 100 - customTotal;
                                                                    const autoCount = prizeCount - customIndices.length;
                                                                    const autoChance = autoCount > 0 ? parseFloat((remaining / autoCount).toFixed(1)) : 0;
                                                                    
                                                                    // Apply auto-calculated chances to non-custom prizes
                                                                    for (let i = 0; i < prizeCount; i++) {
                                                                        if (!customIndices.includes(i)) {
                                                                            np[i] = { ...np[i], chance: autoChance };
                                                                        }
                                                                    }
                                                                    
                                                                    setConfig({ ...config, prizes: np });
                                                                    toast.success('Probabilities applied! Click Save Changes');
                                                                }}
                                                                disabled={customCount === 0}
                                                                style={{ 
                                                                    ...btnPrimary, 
                                                                    padding: '8px 16px', 
                                                                    fontSize: '12px',
                                                                    background: customCount > 0 ? 'rgba(99,102,241,0.8)' : 'rgba(99,102,241,0.3)',
                                                                    cursor: customCount > 0 ? 'pointer' : 'not-allowed',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}
                                                            >
                                                                <Icons.Check /> Apply Probabilities
                                                            </button>
                                                            
                                                            {customCount > 0 && (
                                                                <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-muted)' }}>
                                                                    {customCount}/2 prizes set • Remaining: {(100 - total).toFixed(1)}% for {prizeCount - customCount} prizes
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                );
                                            })()}
                                            
                                            <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '18px', marginTop: 0 }}>Each prize can have multiple images (rotation)</p>
                                            {config.prizes.slice(0, config.prize_count || 12).map((p, i) => (
                                                <div 
                                                    key={i} 
                                                    className="prize-row"
                                                    style={{ 
                                                        position: 'relative',
                                                        border: '1px solid rgba(0,174,239,0.15)',
                                                        borderRadius: '12px', 
                                                        marginBottom: '8px', 
                                                        overflow: 'hidden',
                                                        background: 'rgba(0,174,239,0.08)',
                                                        backdropFilter: 'blur(12px)',
                                                        WebkitBackdropFilter: 'blur(12px)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(20,40,80,0.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px' }}>
                                                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: config.wheel_colors[i], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '13px', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{i + 1}</div>
                                                        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                                                            <input 
                                                                value={p.label} 
                                                                onChange={e => { 
                                                                    if (e.target.value.length <= 20) {
                                                                        const np = [...config.prizes]; 
                                                                        np[i].label = e.target.value; 
                                                                        setConfig({ ...config, prizes: np }); 
                                                                    }
                                                                }} 
                                                                maxLength={20}
                                                                style={{ ...dInput, width: '100%', paddingRight: '45px', background: 'rgba(255,255,255,0.8)' }} 
                                                                placeholder="Prize name"
                                                            />
                                                            <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', color: p.label.length >= 16 ? 'var(--warning)' : 'var(--text-muted)' }}>{p.label.length}/20</span>
                                                        </div>
                                                        <span style={{ 
                                                            padding: '4px 10px', 
                                                            background: p.file_ids?.length > 0 ? 'var(--success-bg)' : ('rgba(0,0,0,0.04)'), 
                                                            color: p.file_ids?.length > 0 ? 'var(--success)' : 'var(--text-muted)', 
                                                            borderRadius: '16px', 
                                                            fontSize: '11px', 
                                                            fontWeight: 600, 
                                                            whiteSpace: 'nowrap', 
                                                            border: p.file_ids?.length > 0 ? '1px solid var(--success-border)' : '1px solid transparent' 
                                                        }}>{p.file_ids?.length || 0} img</span>
                                                        <label style={{ ...btnSmall, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><Icons.Plus /> Add<input type="file" accept=".jpg,.jpeg,.png,video/mp4" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadFile(e.target.files[0], 'prize', i)} /></label>
                                                        <button onClick={() => openPrizeVaultPicker(i)} style={{ ...btnSmall, display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--accent)', fontSize: '11px' }}><Icons.Lock /> Vault</button>
                                                    </div>
                                                    {p.file_ids?.length > 0 && (
                                                        <div style={{ padding: '10px 14px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                                            {p.file_ids.map((f, j) => (
                                                                <div key={j} style={{ position: 'relative', width: '60px', height: '60px' }}>
                                                                    <div style={{ width: '56px', height: '56px', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                                                        {p.types?.[j] === 'video' ? (
                                                                            <video 
                                                                                src={thumbnails[f]} 
                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                muted
                                                                                onLoadedData={(e) => { e.target.currentTime = 0.1; }}
                                                                            />
                                                                        ) : thumbnails[f] ? (
                                                                            <img src={thumbnails[f]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                                                        ) : (
                                                                            <div style={{ width: '100%', height: '100%', background: 'var(--surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>{p.types?.[j] === 'video' ? <Icons.Image /> : <Icons.Image />}</div>
                                                                        )}
                                                                    </div>
                                                                    <button onClick={() => rmImg(i, j)} style={{ position: 'absolute', top: '-4px', right: '0px', width: '20px', height: '20px', borderRadius: '50%', background: '#ef4444', color: '#fff', border: '2px solid rgba(0,0,0,0.3)', cursor: 'pointer', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                                                                </div>
                                                            ))}
                                                            <label style={{ width: '56px', height: '56px', borderRadius: '6px', border: '2px dashed var(--accent)', background: 'rgba(0,174,239,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--accent)', fontSize: '22px', fontWeight: 300, transition: 'all 0.15s ease' }} title="Add image">
                                                                <Icons.Plus />
                                                                <input type="file" accept=".jpg,.jpeg,.png,video/mp4" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadFile(e.target.files[0], 'prize', i)} />
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {activeTab === 'colors' && (
                                        <div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                                {[{ k: 'background_color_1', l: 'Background Start' }, { k: 'background_color_2', l: 'Background End' }, { k: 'button_color', l: 'Spin Button' }, { k: 'button_hover_color', l: 'Button Hover' }, { k: 'text_color', l: 'Text Color' }, { k: 'wheel_border_color', l: 'Wheel Border' }].map(({ k, l }) => (
                                                    <div key={k}>
                                                        <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>{l}</label>
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <input type="color" value={config[k]} onChange={e => setConfig({ ...config, [k]: e.target.value })} style={{ width: '44px', height: '38px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'var(--input-bg)', padding: '2px' }} />
                                                            <input value={config[k]} onChange={e => setConfig({ ...config, [k]: e.target.value })} style={{ ...dInput, flex: 1, fontFamily: 'monospace', fontSize: '12px', minWidth: 0 }} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'block', color: 'var(--text-secondary)' }}>Wheel Segment Colors</label>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
                                                {config.wheel_colors.slice(0, config.prize_count || 12).map((c, i) => (
                                                    <div key={i} style={{ textAlign: 'center' }}>
                                                        <input type="color" value={c} onChange={e => { const nc = [...config.wheel_colors]; nc[i] = e.target.value; setConfig({ ...config, wheel_colors: nc }); }} style={{ width: '100%', height: '32px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'var(--input-bg)', padding: '2px' }} />
                                                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{i + 1}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'packages' && (
                                        <div>
                                            {config.buy_packages.map((pk, i) => (
                                                <div
                                                    key={i}
                                                    className="package-row"
                                                    style={{
                                                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                                                        padding: '12px 14px',
                                                        border: '1px solid rgba(0,174,239,0.15)',
                                                        borderRadius: '12px',
                                                        marginBottom: '8px',
                                                        background: 'rgba(0,174,239,0.08)',
                                                        backdropFilter: 'blur(12px)',
                                                        WebkitBackdropFilter: 'blur(12px)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(20,40,80,0.1)';
                                                        const delBtn = e.currentTarget.querySelector('.delete-btn');
                                                        if (delBtn) delBtn.style.opacity = '1';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                        const delBtn = e.currentTarget.querySelector('.delete-btn');
                                                        if (delBtn) delBtn.style.opacity = '0';
                                                    }}
                                                >
                                                    {/* Badge — with spacer label to align with inputs */}
                                                    <div style={{ flexShrink: 0 }}>
                                                        <div style={{ fontSize: '10px', marginBottom: '3px', visibility: 'hidden' }}>&nbsp;</div>
                                                        <div style={{
                                                            width: '28px', height: '28px', borderRadius: '8px',
                                                            background: 'linear-gradient(135deg, var(--accent), rgba(0,174,239,0.8))',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: '#fff', fontWeight: 700, fontSize: '12px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                        }}>{i + 1}</div>
                                                    </div>
                                                    {/* Label */}
                                                    <div style={{ flex: '1 1 100px', minWidth: 0, maxWidth: '180px' }}>
                                                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px', fontWeight: 500 }}>Label</div>
                                                        <input value={pk.label} placeholder="Label" onChange={e => { const np = [...config.buy_packages]; np[i].label = e.target.value; setConfig({ ...config, buy_packages: np }); }} style={{ ...dInput, background: 'rgba(255,255,255,0.8)', padding: '8px 10px', fontSize: '13px' }} />
                                                    </div>
                                                    {/* Spins */}
                                                    <div style={{ flex: '0 0 50px' }}>
                                                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '3px', fontWeight: 500 }}>Spins</div>
                                                        <input type="number" min="1" value={pk.spins} onChange={e => { const np = [...config.buy_packages]; np[i].spins = parseInt(e.target.value) || 1; setConfig({ ...config, buy_packages: np }); }} style={{ ...dInput, background: 'rgba(255,255,255,0.8)', padding: '8px 6px', fontSize: '13px', textAlign: 'center' }} />
                                                    </div>
                                                    {/* USD */}
                                                    <div style={{ flex: '0 0 60px' }}>
                                                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '3px', fontWeight: 500 }}>$ USD</div>
                                                        <input type="number" min="3" step="0.01" value={pk.price_usd || ''} placeholder="10" onChange={e => { const np = [...config.buy_packages]; np[i].price_usd = parseFloat(e.target.value) || 0; setConfig({ ...config, buy_packages: np }); }} style={{ ...dInput, background: 'rgba(255,255,255,0.8)', padding: '8px 6px', fontSize: '13px', textAlign: 'center' }} />
                                                    </div>
                                                    {/* Popular — with spacer to align */}
                                                    <div style={{ flexShrink: 0 }}>
                                                        <div style={{ fontSize: '10px', marginBottom: '3px', visibility: 'hidden' }}>&nbsp;</div>
                                                        <button
                                                            onClick={() => { const np = [...config.buy_packages]; np.forEach((p, j) => p.popular = j === i ? !p.popular : false); setConfig({ ...config, buy_packages: np }); }}
                                                            title={pk.popular ? 'Remove popular badge' : 'Set as most popular'}
                                                            style={{
                                                                padding: '6px 8px', border: '1px solid', borderRadius: '8px', cursor: 'pointer',
                                                                fontSize: '11px', fontWeight: 600, fontFamily: fontStack, whiteSpace: 'nowrap',
                                                                transition: 'all 0.2s ease',
                                                                background: pk.popular ? 'rgba(234,179,8,0.12)' : 'transparent',
                                                                borderColor: pk.popular ? 'rgba(234,179,8,0.4)' : 'var(--border)',
                                                                color: pk.popular ? '#ca8a04' : 'var(--text-muted)',
                                                            }}
                                                        >{pk.popular ? '★' : '☆'}</button>
                                                    </div>
                                                    {/* Delete — with spacer to align */}
                                                    <div style={{ flexShrink: 0 }}>
                                                        <div style={{ fontSize: '10px', marginBottom: '3px', visibility: 'hidden' }}>&nbsp;</div>
                                                        <button
                                                            className="delete-btn"
                                                            onClick={() => config.buy_packages.length > 1 && setConfig({ ...config, buy_packages: config.buy_packages.filter((_, j) => j !== i) })}
                                                            style={{
                                                                padding: '6px', background: 'transparent', color: 'var(--text-muted)',
                                                                border: '1px solid var(--border)', borderRadius: '8px',
                                                                cursor: config.buy_packages.length > 1 ? 'pointer' : 'not-allowed',
                                                                opacity: 0, transition: 'all 0.2s ease', fontSize: '14px',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.borderColor = 'var(--error)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                                        ><Icons.X /></button>
                                                    </div>
                                                </div>
                                            ))}
                                            {config.buy_packages.length < 6 && (
                                                <button
                                                    onClick={() => setConfig({ ...config, buy_packages: [...config.buy_packages, { id: Date.now(), spins: 1, price: 0, price_usd: 10, label: '1 Spin', popular: false }] })}
                                                    style={{ 
                                                        padding: '14px', 
                                                        background: 'rgba(0,174,239,0.05)', 
                                                        border: '1px dashed rgba(0,174,239,0.3)', 
                                                        borderRadius: '12px', 
                                                        cursor: 'pointer', 
                                                        width: '100%', 
                                                        color: 'var(--accent)', 
                                                        fontSize: '13px', 
                                                        fontFamily: fontStack,
                                                        fontWeight: 500,
                                                        transition: 'all 0.2s ease',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = 'rgba(0,174,239,0.1)';
                                                        e.currentTarget.style.borderColor = 'var(--accent)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = 'rgba(0,174,239,0.05)';
                                                        e.currentTarget.style.borderColor = 'rgba(0,174,239,0.3)';
                                                    }}
                                                >
                                                    <Icons.Plus /> Add Package ({config.buy_packages.length}/6)
                                                </button>
                                            )}
                                            {config.buy_packages.length >= 6 && (
                                                <div style={{ 
                                                    padding: '14px', 
                                                    textAlign: 'center', 
                                                    color: 'var(--text-muted)', 
                                                    fontSize: '13px',
                                                    background: 'rgba(0,0,0,0.02)',
                                                    borderRadius: '10px',
                                                }}>
                                                    Maximum 6 packages reached
                                                </div>
                                            )}
                                        </div>
                                    )}
                                  </div>
                                  {/* Webapp Preview */}
                                  <div style={{ flex: '0 0 260px', position: 'sticky', top: '16px', alignSelf: 'flex-start' }}>
                                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', textAlign: 'center' }}>Live Preview</div>
                                    <div style={{
                                        background: `linear-gradient(135deg, ${config.background_color_1 || '#101923'} 0%, ${config.background_color_2 || '#101923'} 100%)`,
                                        borderRadius: '16px', padding: '16px 12px', textAlign: 'center',
                                        border: '1px solid var(--border)', overflow: 'hidden',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    }}>
                                        {/* Title */}
                                        {config.app_title && <div style={{ color: config.text_color || '#fff', fontSize: '13px', fontWeight: 700, marginBottom: '10px', letterSpacing: '-0.3px' }}>{config.app_title}</div>}
                                        {/* Wheel */}
                                        <div style={{ position: 'relative', width: '180px', height: '180px', margin: '0 auto 20px' }}>
                                            {/* Pointer */}
                                            <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: `16px solid ${config.wheel_border_color || '#00E701'}`, zIndex: 11 , filter: `drop-shadow(0 0 4px ${config.wheel_border_color || '#00E701'}44)` }} />
                                            <canvas ref={wheelCanvasRef} width={200} height={200} style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'block', boxSizing: 'border-box', border: `3px solid ${config.wheel_border_color || '#00E701'}`, boxShadow: `0 0 20px ${config.wheel_border_color || '#00E701'}33` }} />
                                            {/* Center dot */}
                                            <div style={{ position: 'absolute', width: '22px', height: '22px', borderRadius: '50%', background: config.background_color_1 || '#101923', border: `2px solid ${config.wheel_border_color || '#00E701'}`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, boxShadow: `0 0 8px ${config.wheel_border_color || '#00E701'}44` }} />
                                        </div>
                                        {/* Buy buttons */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px', justifyContent: 'center' }}>
                                            {(config.buy_packages || []).map((pkg, idx) => (
                                                <div key={idx} style={{
                                                    flex: '1 1 0', minWidth: '60px', maxWidth: '90px', position: 'relative',
                                                    background: 'rgba(255,255,255,0.08)', border: `1px solid ${pkg.popular ? (config.button_color || '#00E701') : 'rgba(255,255,255,0.12)'}`,
                                                    borderRadius: '7px', padding: '8px 4px', cursor: 'default',
                                                    boxShadow: pkg.popular ? `0 0 8px ${config.button_color || '#00E701'}22` : 'none',
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {pkg.popular && <div style={{ position: 'absolute', top: '-7px', left: '50%', transform: 'translateX(-50%)', background: config.button_color || '#00E701', color: config.background_color_1 || '#101923', fontSize: '6px', fontWeight: 700, padding: '1px 5px', borderRadius: '4px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Most Popular</div>}
                                                    <div style={{ fontSize: '9px', fontWeight: 600, color: config.text_color || '#fff', textAlign: 'center' }}>{pkg.label}</div>
                                                    <div style={{ fontSize: '8px', color: '#10b981', fontWeight: 500, marginTop: '2px', textAlign: 'center' }}>${pkg.price_usd || (pkg.price / 77).toFixed(2)}</div>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Spin button */}
                                        <div style={{
                                            background: config.button_color || '#00E701', color: config.background_color_1 || '#101923',
                                            padding: '8px', borderRadius: '7px', fontWeight: 700, fontSize: '10px',
                                            textTransform: 'uppercase', letterSpacing: '0.5px',
                                            boxShadow: `0 2px 12px ${config.button_color || '#00E701'}44`,
                                        }}>SPIN</div>
                                        {/* Warning */}
                                        <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.4)', marginTop: '6px', fontWeight: 500 }}>Buy spins to play</div>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ padding: '0 22px 22px' }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <button onClick={saveConfig} disabled={saving} style={{ ...btnPrimary, flex: 1, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
                                        <button onClick={() => setShowResetConfirm(true)} style={{ ...btnSecondary, padding: '10px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}>Reset to Default</button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    </div>
                </div>
                )}
            </div>

            {/* Prize Vault Picker Modal */}
            {prizeVaultPicker !== null && (
                <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
                    onClick={() => setPrizeVaultPicker(null)}>
                    <div style={{ background: 'var(--modal-bg)', border: '1px solid var(--glass-border)', borderRadius: '16px', width: '500px', maxWidth: '90vw', maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)', margin: 0 }}><Icons.Lock /> Select from Vault</h3>
                            <button onClick={() => setPrizeVaultPicker(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>×</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                            {prizeVaultItems.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>No media in vault. Upload from the Vault tab first.</div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px' }}>
                                    {prizeVaultItems.map(item => (
                                        <div key={item.id} onClick={() => attachVaultToPrize(item)}
                                            style={{ borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', border: '2px solid transparent', transition: 'border 0.15s', aspectRatio: '1', position: 'relative', background: 'rgba(0,0,0,0.03)' }}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                                            {item.file_type === 'video' ? (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
                                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(255,255,255,0.8)"><polygon points="5 3 19 12 5 21"/></svg>
                                                </div>
                                            ) : (
                                                <img src={`${API_URL}/api/dashboard/file?auth=${encodeURIComponent(localStorage.getItem('token')||'')}&file_id=${encodeURIComponent(item.file_id)}&thumb=1`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            )}
                                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '3px 5px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', fontSize: '8px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.title || item.original_filename || 'Untitled'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal {...confirmProps} />

            {/* Connect Telegram Modal */}
            {showTgConnect && !isChatter && (
                <div onClick={() => setShowTgConnect(false)} style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card-bg)', borderRadius: '16px', padding: '32px 28px', maxWidth: '420px', width: '100%', boxShadow: '0 16px 48px rgba(0,0,0,0.18)', fontFamily: fontStack, textAlign: 'center' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Icons.Send style={{ color: 'var(--accent)' }} />
                        </div>
                        <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginBottom: '6px' }}>Connect Telegram</h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
                            Link your Telegram to manage channels and receive notifications
                        </p>

                        {connectCode ? (
                            <>
                                <div style={{ background: 'var(--surface-muted)', border: '2px solid var(--border)', borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
                                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Send this to the bot</div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', fontFamily: 'monospace', marginBottom: '10px', userSelect: 'all' }}>/connect {connectCode}</div>
                                    <button onClick={() => { navigator.clipboard.writeText(`/connect ${connectCode}`); setTgConnectCopied(true); setTimeout(() => setTgConnectCopied(false), 2000); }}
                                        style={{ ...btnSmall, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        {tgConnectCopied ? <><Icons.Check /> Copied!</> : <><Icons.Copy /> Copy</>}
                                    </button>
                                </div>

                                <div style={{ background: 'var(--surface-muted)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>1</div>
                                    <div><div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>Open @{BOT_USERNAME} on Telegram</div></div>
                                </div>
                                <div style={{ background: 'var(--surface-muted)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left' }}>
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>2</div>
                                    <div><div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>Send <span style={{ fontFamily: 'monospace' }}>/connect {connectCode}</span></div></div>
                                </div>

                                <a href={`https://t.me/${BOT_USERNAME}?start=connect_${connectCode}`}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ ...btnPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', textDecoration: 'none', marginBottom: '16px', width: 'auto' }}>
                                    <Icons.Send /> Open Telegram
                                </a>
                            </>
                        ) : (
                            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
                                    {tgConnectLoading ? 'Loading connect code...' : 'Could not load connect code'}
                                </p>
                                {!tgConnectLoading && (
                                    <button onClick={fetchConnectCode} style={{ ...btnSmall, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                        Retry
                                    </button>
                                )}
                            </div>
                        )}

                        <button onClick={() => setShowTgConnect(false)}
                            style={{ ...btnGhost, color: 'var(--text-muted)', width: '100%' }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
}